import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useWebSocket } from "../context/WebSocketContext";
import { useSearch } from "../context/SearchContext";
import {copyToClipboard, useWindowWidth} from "./GlobalFunctions";
import SnipeModal from "./SnipeModal";
import { ArrowDown, ArrowUp } from "lucide-react";
import imageLogoWebsite from "../images/webp/logo-website.webp";
import imageLogoTelegram from "../images/webp/logo-telegram.webp";
import imageLogoX from "../images/webp/logo-x.webp";
import imageLogoGitBook from "../images/webp/logo-gitBook.webp";
import imageTokenImageNotSupplied from "../images/webp/token-card-no-image-provided.webp";
import imageButtonCopySmall from "../images/webp/icon-copy-green.webp";
import imageLogoBanana from '../images/webp/logo-banana-black.webp';
import imageDancingBanana2 from "../images/webp/dancing-banana-2.webp";
import imageDancingBanana3 from "../images/webp/dancing-banana-3.webp";
import imageWordTrade from "../images/webp/word-trade.webp";
import imageWordLaunch from "../images/webp/word-launch.webp";
import imageWordEarn from "../images/webp/word-earn.webp";
import PremiumCard from "./premium/PremiumCard";
import DegenHero from "./premium/DegenHero";
import FeaturedTokens from "./premium/TrendingSection";
import FeaturedRow from "./premium/FeaturedRow";
import LiveFeed from "./premium/LiveFeed";
import { useTokenFeed } from "../lib/useTokenFeed";
import { slurpWs } from "../lib/slurpApi";

// Grid pagination — render this many tokens per page on the front grid.
const TOKENS_PER_PAGE = 20;
// EVM stack ripped out — slurp is Solana-only. The legacy buy/sell flow
// referenced wagmi+ethers (BSC era); those code paths are unreachable
// from the current UI but the imports were still being pulled into the
// bundle. These stubs return safe defaults so the dead branches still
// parse, while the actual Solana flow goes through PremiumDetail and
// @solana/wallet-adapter elsewhere.
const useAccount = () => ({ address: undefined, isConnected: false, chainId: undefined });
const useWalletClient = () => ({ data: undefined });
const ethers = {
  BrowserProvider: function () { throw new Error("ethers stripped from bundle"); },
  parseEther: () => { throw new Error("ethers stripped from bundle"); },
};

// Shake/flash duration for a Recent-Trades reorder, in ms.
const BUMP_TTL_MS = 1500;
// New-launch shake duration, in ms.
const LAUNCH_SHAKE_MS = 4000;

// 0x API configuration for direct swaps
const FEE_RECIPIENT = "0xC475FF67b7ABf80D75a91618b9a431B4a709B059";
const FEE_BPS = 100; // 1% fee

// Get chain ID for 0x API
const getChainId = (chain) => {
  switch (chain?.toLowerCase()) {
    case 'eth':
    case 'ethereum':
      return '1';
    case 'base':
      return '8453';
    case 'bsc':
    case 'binance':
      return '56';
    default:
      return '8453'; // Default to Base
  }
};

// Get 0x token address format
const get0xTokenAddress = (chain, isNative = false) => {
  if (isNative) {
    return "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE"; // Native ETH/BNB representation for 0x
  }
  return chain; // Token contract address
};

// Get 0x swap quote via backend proxy
const get0xQuote = async (chain, sellToken, buyToken, sellAmount, takerAddress) => {
  const isSellingToken = sellToken !== "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";
  const feeToken = isSellingToken ? buyToken : sellToken; // Always collect fee in ETH
  
  const params = new URLSearchParams({
    chainId: getChainId(chain),
    sellToken,
    buyToken,
    sellAmount: sellAmount.toString(),
    taker: takerAddress,
    swapFeeRecipient: FEE_RECIPIENT,
    swapFeeBps: FEE_BPS.toString(),
    swapFeeToken: feeToken,
  });

  const proxyURL = `https://bnb-pad-backend.onrender.com/api/0x-quote/${chain}?${params}`;
  const response = await fetch(proxyURL);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(`0x API error: ${errorData.error || response.statusText}`);
  }
  
  return await response.json();
};

// Get 0x swap transaction data via backend proxy
const get0xSwap = async (chain, sellToken, buyToken, sellAmount, takerAddress) => {
  const isSellingToken = sellToken !== "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";
  const feeToken = isSellingToken ? buyToken : sellToken;
  
  const params = new URLSearchParams({
    chainId: getChainId(chain),
    sellToken,
    buyToken,
    sellAmount: sellAmount.toString(),
    taker: takerAddress,
    swapFeeRecipient: FEE_RECIPIENT,
    swapFeeBps: FEE_BPS.toString(),
    swapFeeToken: feeToken,
  });

  const proxyURL = `https://bnb-pad-backend.onrender.com/api/0x-swap/${chain}?${params}`;
  const response = await fetch(proxyURL);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(`0x API error: ${errorData.error || response.statusText}`);
  }
  
  return await response.json();
};

// Currency Formatting Utility
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "N/A";

  const num = parseFloat(value);
  if (isNaN(num)) return "N/A";

  // For very large numbers (billions), still use suffix
  if (num >= 1000000000) {
    return `$${Math.round(num / 1000000000).toLocaleString()}B`;
  }

  // For prices (small numbers), show more decimals
  if (num === 0)
    return "$0";
  else if (num < 0.01) {
    return `$${num.toFixed(8)}`;
  } else if (num < 1) {
    return `$${num.toFixed(4)}`;
  }

  // For all other numbers (including market caps), show full number with commas
  return `$${Math.round(num).toLocaleString()}`;
};

// Function to fetch fresh volume and market cap data from DexScreener
const fetchFreshTokenData = async (contractAddress, chain) => {
  try {
    console.log(`🔄 Fetching fresh data for token ${contractAddress} on ${chain}`);
    
    // Map chain to DexScreener format
    const chainMapping = {
      'Base': 'base',
      'Eth': 'ethereum', 
      'Bsc': 'bsc',
      'base': 'base',
      'eth': 'ethereum',
      'bsc': 'bsc'
    };
    
    const dexChain = chainMapping[chain] || 'base';
    
    // Try DexScreener API
    const response = await fetch(
      `https://api.dexscreener.com/latest/dex/tokens/${contractAddress}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      }
    );

    if (!response.ok) {
      console.log(`❌ DexScreener API failed with status: ${response.status}`);
      return null;
    }

    const data = await response.json();
    
    if (data && data.pairs && data.pairs.length > 0) {
      // Find the best pair (highest volume on the correct chain)
      const chainPairs = data.pairs.filter(pair => 
        pair.chainId === dexChain
      );
      
      const bestPair = chainPairs.length > 0 ? chainPairs[0] : data.pairs[0];
      
      if (bestPair) {
        console.log(`✅ Fresh data found for token: Volume=$${bestPair.volume?.h24 || 0}, MC=$${bestPair.fdv || 0}`);
        
        return {
          volume_usd_24h: parseFloat(bestPair.volume?.h24 || 0),
          market_cap_usd: parseFloat(bestPair.fdv || 0),
          _freshData: true
        };
      }
    }
    
    console.log(`ℹ️ No trading pairs found for token ${contractAddress} on DexScreener (this is normal for some tokens)`);
    return null;
    
  } catch (error) {
    console.error(`❌ Error fetching fresh data for ${contractAddress}:`, error);
    return null;
  }
};

// Updated TokenCard Component with background image
const TokenCard = React.memo(
  ({ token, highlight = false, shouldGlow = false, snipeMode = false, snipeSettings = {}, onSnipeBuy = null, tokenDetails = {}, loadingTokenDetails = {}, fetchTokenDetails = null }) => {
    const navigate = useNavigate();
    // Dynamic DexScreener link based on chain
    const getDexScreenerLink = () => {
      if (token.chain === "Eth") {
        return `https://dexscreener.com/ethereum/${token.contractAddress}`;
      }
      // Default to base chain for other tokens
      return `https://dexscreener.com/base/${token.contractAddress}`;
    };

    const dexScreenerLink = getDexScreenerLink();

    const [isAnimating, setIsAnimating] = useState(false);
    const [prevPrice, setPrevPrice] = useState(token.price_usd);
    const cardRef = useRef(null);




    // Track price changes to trigger animation
    useEffect(() => {
      // Only animate if we have a previous price and it's different

      if (
        prevPrice !== null &&
        token.price_usd !== null &&
        prevPrice !== token.price_usd
      ) {
        // Determine if price went up or down
        const direction = token.price_usd > prevPrice ? "up" : "down";
        // Set animation state with direction

        setIsAnimating(direction);

        // Reset animation after a delay
        const timer = setTimeout(() => {
          setIsAnimating(false);
        }, 2000); // Reduced animation duration for better performance

        return () => clearTimeout(timer);
      }

      // Update previous price for next comparison
      setPrevPrice(token.price_usd);
    }, [token.price_usd, prevPrice]);

    const handleCardClick = useCallback(
      (e) => {
        if (e.target.closest(".dexscreener-link-new")) return;
        navigate(`/token/${token.contractAddress}`);
      },
      [navigate, token.contractAddress]
    );

    // Determine animation class based on price change direction
    const animationClass =
      isAnimating === "up"
        ? "price-up-animation"
        : isAnimating === "down"
        ? "price-down-animation"
        : "";

    const checkTokenNameSize = (name) => {
      if (!name) return '';
      
      // Split the name into words and find the longest word length
      const longestWordLength = name.split(/\s+/).reduce((max, word) => {
        return Math.max(max, word.length);
      }, 0);
      
      var tokenClass = "";
      // Return appropriate class based on longest word length
      if (longestWordLength > 12 || name.length > 26) {
        tokenClass = 'token-card-name-xs';
      } else if (longestWordLength > 9 || name.length > 20) {
        tokenClass = 'token-card-name-s';
      } else if(name.length > 12){
        tokenClass = 'token-card-name-m';
      }
      return tokenClass;
    };
    return (
      <div
        ref={cardRef}
        className={`token-card-new mouse-pointer ${
          highlight ? "highlight-card" : ""
        } ${animationClass} ${shouldGlow ? "first-token-glow" : ""}`}
        // style={{
        //   "--token-image-url": token.image?.url
        //     ? `url(${token.image.url})`
        //     : `url(${imageTokenImageNotSupplied})`,
        // }}
        onClick={handleCardClick}
        data-token-address={token.contractAddress}
        data-animating={isAnimating ? "true" : "false"}
      >
        <div className = "token-card-image-container">
          <img src={token.image?.url || imageTokenImageNotSupplied} alt={token.name} className="token-card-image" />
        </div>
        <div className ="token-card-info-container"> 
        
        <div className="token-card-header-row">
          <h3 className = {`${checkTokenNameSize(token.name)}  `}> {token.name}</h3>
          
          <div className ="token-card-socials-container">
            {(() => {
              const details = tokenDetails[token.contractAddress?.toLowerCase()];
              const website = details?.website || token.website;
              const telegram = details?.telegram || token.telegram;
              const twitter = details?.twitter || token.twitter;
              
              // Trigger fetch if we don't have details yet
              if (!details && !loadingTokenDetails[token.contractAddress?.toLowerCase()] && fetchTokenDetails) {
                fetchTokenDetails(token.contractAddress);
              }
              
              return (
                <>
                  {website && (
                    <div className="token-card-socials-item hover-grow">
                      <a href={website.startsWith("http") ? website : `https://${website}`} target="_blank" rel="noopener noreferrer">
                        <img className="token-card-socials-img website" src={imageLogoWebsite} alt="Website" />
                      </a>
                    </div>
                  )}
                  {telegram && (
                    <div className="token-card-socials-item hover-grow">
                      <a href={telegram} target="_blank" rel="noopener noreferrer">
                        <img className="token-card-socials-img telegram" src={imageLogoTelegram} alt="Telegram" />
                      </a>
                    </div>
                  )}
                  {twitter && (
                    <div className="token-card-socials-item hover-grow">
                      <a href={twitter} target="_blank" rel="noopener noreferrer">
                        <img className="token-card-socials-img x" src={imageLogoX} alt="Twitter" />
                      </a>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

          {/* <div className="token-card-header-row-button-container">
            <img
              className="copy-contract-address-button button-main"
              src={imageCopyContractAddress}
              alt="Copy Contract Address"
              onClick = {(e) =>
              { e.stopPropagation();
                 copyToClipboard(token.contractAddress)}
              }/>


         
            {snipeMode && (
              <button
                className={`token-card-buy-button ${snipeMode ? 'active-mode' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSnipeBuy) {
                    onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain);
                  }
                }}
                title={`Buy on ${token.chain || 'Unknown'} network`}
              >
                Buy {snipeSettings.defaultEthAmount || '0.01'}
              </button>
            )}



            <a
              href={dexScreenerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="dexscreener-link-new"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                className="token-card-dexscreener-button button-main"
                src={imageDexscreenerButton}
                alt="DexScreener"
              />
            </a>
          </div> */}

        <div className="token-card-details-container">
          <div className="token-card-details-row">
            <div className="token-card-details-row-label">
              Market Cap:
            </div>
            <div className="token-card-details-row-value">
              {formatCurrency(token.market_cap_usd)}
            </div>
          </div>

          <div className="token-card-details-row">
            <div className="token-card-details-row-label">
            Volume:
            </div>
            <div className="token-card-details-row-value">
              {formatCurrency(token.volume_usd_24h)}
            </div>
          </div>
        </div>

        <div className="token-card-quick-buy-container">
        <button
          className={`token-card-quick-buy-button hover-grow ${snipeMode ? 'active-mode' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (onSnipeBuy) {
              onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain, '0.2');
            }
          }}
          title={`Buy on ${token.chain || 'Unknown'} network`}
        >
          BUY 0.2
        </button>

        <button
          className={`token-card-quick-buy-button hover-grow ${snipeMode ? 'active-mode' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (onSnipeBuy) {
              onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain, '0.5');
            }
          }}
          title={`Buy on ${token.chain || 'Unknown'} network`}
        >
          BUY 0.5
        </button>

        <button
          className={`token-card-quick-buy-button hover-grow ${snipeMode ? 'active-mode' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (onSnipeBuy) {
              onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain, '1.0');
            }
          }}
          title={`Buy on ${token.chain || 'Unknown'} network`}
        >
          BUY 1.0
        </button>

        <button
          className={`token-card-copy-button hover-grow`}
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(token.contractAddress);
          }}
          title="Copy Token Address"
        >
          <img className="token-card-copy-button-img" src={imageButtonCopySmall} alt="Copy" />
        </button>
        </div>
        {/* <p className="token-card-volume">Volume: {formatCurrency(token.volume_usd_24h)}</p> */}

          {/* <div className="token-card-details-row-left">
          {token.chain ? (
            token.chain === "Eth" ? (
              <img src={imageLogoEtherium} alt="Ethereum" className="token-card-chain-logo" />
            ) : token.chain === "Base" ? (
              <img src={imageLogoBase} alt="Base" className="token-card-chain-logo" />
            ) :token.chain === "Bsc" ?( 
              <img src={imageLogoBsc} alt="Bsc" className="token-card-chain-logo" />
            ) : 
            <img src={imageLogoQuestionMark} alt="Unknown" className="token-card-chain-logo" />
          ): (
            <img src={imageLogoQuestionMark} alt="Unknown" className="token-card-chain-logo" />

          )}
          <p className={`token-card-symbol-name ${getSymbolSizeClass(token.symbol)}`}>${token.symbol}</p>
          </div> */}

          {/* <div className="token-card-details-row-right">
            <p className="token-card-market-cap">MC: {formatCurrency(token.market_cap_usd)}</p>
            <p className="token-card-volume">Vol: {formatCurrency(token.volume_usd_24h)}</p>
          </div> */}

      </div>
      </div>
    );

    // return (
    //   <div
    //     ref={cardRef}
    //     className={`token-card-new mouse-pointer ${
    //       highlight ? "highlight-card" : ""
    //     } ${animationClass} ${shouldGlow ? "first-token-glow" : ""}`}
    //     style={{
    //       "--token-image-url": token.image?.url
    //         ? `url(${token.image.url})`
    //         : `url(${imageTokenImageNotSupplied})`,
    //     }}
    //     onClick={handleCardClick}
    //     data-token-address={token.contractAddress}
    //     data-animating={isAnimating ? "true" : "false"}
    //   >
    //     <div className="token-card-header-row">
    //       <h3 className = {`${checkTokenNameSize(token.name)} text-stroke-2 `}> {token.name}</h3>
    //       <div className="token-card-header-row-button-container">
    //         <img
    //           className="copy-contract-address-button button-main"
    //           src={imageCopyContractAddress}
    //           alt="Copy Contract Address"
    //           onClick = {(e) =>
    //           { e.stopPropagation();
    //              copyToClipboard(token.contractAddress)}
    //           }/>
    //         {/* Quick Buy BUY Button */}
    //         {snipeMode && (
    //           <button
    //             className={`token-card-buy-button ${snipeMode ? 'active-mode' : ''}`}
    //             onClick={(e) => {
    //               e.stopPropagation();
    //               if (onSnipeBuy) {
    //                 onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain);
    //               }
    //             }}
    //             title={`Buy on ${token.chain || 'Unknown'} network`}
    //           >
    //             Buy {snipeSettings.defaultEthAmount || '0.01'}
    //           </button>
    //         )}
    //         <a
    //           href={dexScreenerLink}
    //           target="_blank"
    //           rel="noopener noreferrer"
    //           className="dexscreener-link-new"
    //           onClick={(e) => e.stopPropagation()}
    //         >
    //           <img
    //             className="token-card-dexscreener-button button-main"
    //             src={imageDexscreenerButton}
    //             alt="DexScreener"
    //           />
    //         </a>
    //       </div>
    //     </div>

    //     <div className="token-card-details-row">
    //       <div className="token-card-details-row-left">
    //       {token.chain ? (
    //         token.chain === "Eth" ? (
    //           <img src={imageLogoEtherium} alt="Ethereum" className="token-card-chain-logo" />
    //         ) : token.chain === "Base" ? (
    //           <img src={imageLogoBase} alt="Base" className="token-card-chain-logo" />
    //         ) :token.chain === "Bsc" ?( 
    //           <img src={imageLogoBsc} alt="Bsc" className="token-card-chain-logo" />
    //         ) : 
    //         <img src={imageLogoQuestionMark} alt="Unknown" className="token-card-chain-logo" />
    //       ): (
    //         <img src={imageLogoQuestionMark} alt="Unknown" className="token-card-chain-logo" />

    //       )}
    //       <p className={`token-card-symbol-name ${getSymbolSizeClass(token.symbol)}`}>${token.symbol}</p>
    //       </div>

    //       <div className="token-card-details-row-right">
    //         <p className="token-card-market-cap">MC: {formatCurrency(token.market_cap_usd)}</p>
    //         <p className="token-card-volume">Vol: {formatCurrency(token.volume_usd_24h)}</p>
    //       </div>

    //     </div>
    //   </div>
    // );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    const prevDetails = prevProps.tokenDetails[prevProps.token.contractAddress?.toLowerCase()];
    const nextDetails = nextProps.tokenDetails[nextProps.token.contractAddress?.toLowerCase()];
    
    return (
      prevProps.token.contractAddress === nextProps.token.contractAddress &&
      prevProps.token.price_usd === nextProps.token.price_usd &&
      prevProps.token.market_cap_usd === nextProps.token.market_cap_usd &&
      prevProps.token.volume_usd_24h === nextProps.token.volume_usd_24h &&
      prevProps.highlight === nextProps.highlight &&
      prevProps.shouldGlow === nextProps.shouldGlow &&
      prevProps.snipeMode === nextProps.snipeMode &&
      prevProps.snipeSettings.defaultEthAmount === nextProps.snipeSettings?.defaultEthAmount &&
      prevDetails?.website === nextDetails?.website &&
      prevDetails?.telegram === nextDetails?.telegram &&
      prevDetails?.twitter === nextDetails?.twitter
    );
  }
);

// DEX Table Component
const DexTable = React.memo(({ tokens, snipeMode, snipeSettings, onSnipeBuy, addListener, removeListener, emit, setShowDisclaimerModal }) => {
  // Debug logging removed for cleaner console

  // State for DEX Paid data
  const [dexPaidData, setDexPaidData] = useState({});
  const [loadingDexPaid, setLoadingDexPaid] = useState({});
  
  // State for token details (social media, age, etc.)
  const [tokenDetails, setTokenDetails] = useState({});
  const [loadingTokenDetails, setLoadingTokenDetails] = useState({});

  // Function to get chain ID for DexScreener API
  const getDexScreenerChainId = (chain) => {
    const chainMapping = {
      'Base': 'base',
      'Eth': 'ethereum', 
      'Bsc': 'bsc',
      'base': 'base',
      'eth': 'ethereum',
      'bsc': 'bsc'
    };
    return chainMapping[chain] || 'base';
  };

  const windowWidth = useWindowWidth();

  const formatAddress = (address) =>
  {
    if(windowWidth < 850)
    {
      return address.slice(0, 2) + "..." + address.slice(-2);
    }
    return address.slice(0, 4) + "..." + address.slice(-4);
    
  }

  // Function to fetch token details (social media, age, etc.) via WebSocket
  const fetchTokenDetails = useCallback(async (tokenAddress) => {
    if (!tokenAddress) return null;
    
    const cacheKey = tokenAddress.toLowerCase();
    
    // Check if we already have data for this token
    if (tokenDetails[cacheKey]) {
      return tokenDetails[cacheKey];
    }

    // Check if we're already loading this token
    if (loadingTokenDetails[cacheKey]) {
      return null;
    }

    try {
      setLoadingTokenDetails(prev => ({ ...prev, [cacheKey]: true }));
      
      // Use WebSocket to get token details (same as TokenDetailPage)
      // Normalize the token address for comparison
      const normalizedAddress = tokenAddress.toLowerCase();
      
      const wsData = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error('WebSocket request timed out'));
        }, 10000);

        const handleTokenDetails = (data) => {
          // Verify this response is for the correct token address
          const responseAddress = data?.contractAddress?.toLowerCase();
          if (responseAddress !== normalizedAddress) {
            // This response is for a different token, ignore it
            console.log(`⚠️ Ignoring token-details response - expected ${normalizedAddress}, got ${responseAddress}`);
            return;
          }
          
          clearTimeout(timeout);
          cleanup();
          resolve(data);
        };

        const handleError = (error) => {
          clearTimeout(timeout);
          cleanup();
          reject(new Error(error.message || 'Failed to fetch token details'));
        };

        const cleanup = () => {
          removeListener('token-details', handleTokenDetails);
          removeListener('error', handleError);
        };

        // Add listeners
        addListener('token-details', handleTokenDetails);
        addListener('error', handleError);

        // Send request
        emit('get-token-details', { contractAddress: tokenAddress });
      });
      
      // Store the result
      setTokenDetails(prev => ({ ...prev, [cacheKey]: wsData }));
      return wsData;
      
    } catch (error) {
      console.error(`❌ Error fetching token details for ${tokenAddress}:`, error);
      return null;
    } finally {
      setLoadingTokenDetails(prev => ({ ...prev, [cacheKey]: false }));
    }
  }, [tokenDetails, loadingTokenDetails, addListener, removeListener, emit]);

  // Function to fetch DEX Paid data from DexScreener
  const fetchDexPaidData = useCallback(async (tokenAddress, chain) => {
    if (!tokenAddress || !chain) return null;
    
    const chainId = getDexScreenerChainId(chain);
    const cacheKey = `${chainId}_${tokenAddress}`;
    
    // Check if we already have data for this token
    if (dexPaidData[cacheKey]) {
      return dexPaidData[cacheKey];
    }

    // Check if we're already loading this token
    if (loadingDexPaid[cacheKey]) {
      return null;
    }

    try {
      setLoadingDexPaid(prev => ({ ...prev, [cacheKey]: true }));
      
      const response = await fetch(
        `https://api.dexscreener.com/orders/v1/${chainId}/${tokenAddress}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        }
      );

      if (!response.ok) {
        console.log(`❌ DexScreener orders API failed for ${tokenAddress}: ${response.status}`);
        return null;
      }

      const data = await response.json();
      
      // Store the result
      setDexPaidData(prev => ({ ...prev, [cacheKey]: data }));
      return data;
      
    } catch (error) {
      console.error(`❌ Error fetching DEX Paid data for ${tokenAddress}:`, error);
      return null;
    } finally {
      setLoadingDexPaid(prev => ({ ...prev, [cacheKey]: false }));
    }
  }, [dexPaidData, loadingDexPaid]);

  // Format DEX Paid data for display
  const formatDexPaid = (data) => {
    if (!data || !Array.isArray(data)) {
      return 'No';
    }
    
    if (data.length === 0) {
      return 'No';
    }
    
    // If there's any data, show Yes
    return 'Yes';
  };

  const formatAge = (createdAt) => {
    if (!createdAt) return 'Unknown';
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears}y ago`;
  };

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    return `$${parseFloat(price).toFixed(6)}`;
  };

  const formatMarketCap = (mcap) => {
    if (!mcap) return '$0';
    const num = parseFloat(mcap);
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(0)}`;
  };

  const formatVolume = (volume) => {
    if (!volume || volume === 0) return '$0';
    const num = parseFloat(volume);
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(0)}`;
  };

  return (
    <div className="dex-table-container scrollbar-styled">
      <table className="dex-table">
        <thead>
          <tr>
                    <th>Token</th>
                    <th>CA</th>
                    <th>Socials</th>
                    <th>Age</th>
                    <th>MCAP</th>
                    <th>24h</th>
                    <th>Volume</th>
                    <th>Holders</th>
                    <th>DEX Paid</th>
                    <th>Quick Buy</th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((token, index) => (
            <tr 
              key={token.contractAddress || index}
              className="dex-table-row"
              // onClick={() => {
              //   if (token.contractAddress) {
              //     window.open(`/#/token/${token.contractAddress}`, '_blank');
              //   }
              // }}
            >
              <td> 
                <div className="dex-token-main-cell">
              <div className="dex-age-mobile">
                  {(() => {
                    const details = tokenDetails[token.contractAddress?.toLowerCase()];
                    const synth = typeof token.age_seconds === 'number'
                      ? new Date(Date.now() - token.age_seconds * 1000).toISOString()
                      : null;
                    const createdAt = details?.createdAt || token.createdAt || synth;
                    return <span>⏰ {formatAge(createdAt)}</span>;
                  })()}
                </div>
                <span className="dex-token-name-phone">{token.symbol || token.name}</span>

                <div className="dex-token-cell cursor-pointer"
                onClick={() => {
                  if (token.contractAddress) {
                    window.open(`/#/token/${token.contractAddress}`, '_blank');
                  }
                }}>
                  
                  <img 
                    src={token.image?.url || imageTokenImageNotSupplied} 
                    alt={token.name}
                    className="dex-token-icon"
                    onError={(e) => {
                      e.target.src = imageTokenImageNotSupplied;
                    }}
                  />
                  <span className="dex-token-name">{token.symbol || token.name}</span>
                </div>
                <div 
                  className="dex-contract-address-mobile mouse-pointer"
                  onClick = {(e) =>
                    { e.stopPropagation();
                       copyToClipboard(token.contractAddress)}
                    }
                  title="Click to copy contract address"
                >
                  <div className = "dex-contract-address-mobile-large mouse-pointer">
                    {token.contractAddress ? 
                      `${token.contractAddress.slice(0, 4)}...${token.contractAddress.slice(-4)}` : 
                      'N/A'
                    }
                    <img className="dex-contract-address-copy-button-img mouse-pointer" src={imageButtonCopySmall} alt="Copy" />
                  </div>
                  {/* <div className = "dex-contract-address-mobile-small">
                    <img className="dex-contract-address-copy-button-img mouse-pointer" src={imageCopyContractAddress} alt="Copy" />
                  </div> */}
                </div>
                <div className="dex-socials-mobile">
                          {(() => {
                            const details = tokenDetails[token.contractAddress?.toLowerCase()];
                            const website = details?.website || token.website;
                            const telegram = details?.telegram || token.telegram;
                            const twitter = details?.twitter || token.twitter;
                            
                            // Trigger fetch if we don't have details yet
                            if (!details && !loadingTokenDetails[token.contractAddress?.toLowerCase()]) {
                              fetchTokenDetails(token.contractAddress);
                            }
                            
                            return (
                              <>
                                {website && (
                                  <a 
                                    href={website.startsWith("http") ? website : `https://${website}`}
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="dex-social-icon"
                                    title="Website"
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="dex-social-icon-svg"
                                      aria-label="Website"
                                    >
                                      <circle cx="12" cy="12" r="10" />
                                      <path d="M2 12h20" />
                                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                    </svg>
                                  </a>
                                )}
                                {telegram && (
                                  <a 
                                    href={telegram} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="dex-social-icon"
                                    title="Telegram"
                                  >
                                    <img 
                                      src="/images/TG.svg.webp" 
                                      alt="Telegram" 
                                      className="dex-social-icon-img"
                                    />
                                  </a>
                                )}
                                {twitter && (
                                  <a 
                                    href={twitter} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="dex-social-icon"
                                    title="Twitter"
                                  >
                                    <img 
                                      src="/images/x.svg.png" 
                                      alt="Twitter" 
                                      className="dex-social-icon-img"
                                    />
                                  </a>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        </div>     
              </td>
              <td>
                <div 
                  className="dex-contract-address"
                  onClick = {(e) =>
                    { e.stopPropagation();
                       copyToClipboard(token.contractAddress)}
                    }
                  title="Click to copy contract address"
                >
                  {token.contractAddress ? 
                    formatAddress(token.contractAddress) : 
                    'N/A'
                  }
                  <img className="dex-contract-address-copy-button-img" src={imageButtonCopySmall} alt="Copy" />
                </div>
              </td>
                      <td>
                        <div className="dex-socials">
                          {(() => {
                            const details = tokenDetails[token.contractAddress?.toLowerCase()];
                            const website = details?.website || token.website;
                            const telegram = details?.telegram || token.telegram;
                            const twitter = details?.twitter || token.twitter;
                            
                            // Trigger fetch if we don't have details yet
                            if (!details && !loadingTokenDetails[token.contractAddress?.toLowerCase()]) {
                              fetchTokenDetails(token.contractAddress);
                            }
                            
                            return (
                              <>
                                {website && (
                                  <a 
                                    href={website.startsWith("http") ? website : `https://${website}`}
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="dex-social-icon"
                                    title="Website"
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="dex-social-icon-svg"
                                      aria-label="Website"
                                    >
                                      <circle cx="12" cy="12" r="10" />
                                      <path d="M2 12h20" />
                                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                    </svg>
                                  </a>
                                )}
                                {telegram && (
                                  <a 
                                    href={telegram} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="dex-social-icon"
                                    title="Telegram"
                                  >
                                    <img 
                                      src="/images/TG.svg.webp" 
                                      alt="Telegram" 
                                      className="dex-social-icon-img"
                                    />
                                  </a>
                                )}
                                {twitter && (
                                  <a 
                                    href={twitter} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="dex-social-icon"
                                    title="Twitter"
                                  >
                                    <img 
                                      src="/images/x.svg.png" 
                                      alt="Twitter" 
                                      className="dex-social-icon-img"
                                    />
                                  </a>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        <span className="dex-age">
                          {(() => {
                            const details = tokenDetails[token.contractAddress?.toLowerCase()];
                            const synth = typeof token.age_seconds === 'number'
                              ? new Date(Date.now() - token.age_seconds * 1000).toISOString()
                              : null;
                            const createdAt = details?.createdAt || token.createdAt || synth;
                            return formatAge(createdAt);
                          })()}
                        </span>
                      </td>
              <td>
                <span className="dex-volume">{formatMarketCap(token.market_cap_usd)}</span>
                <div className="dex-volume-mobile">
                  <div className="dex-volume-mobile-item">
                    <span className="dex-volume-mobile-label">MCAP:</span>
                   <span className="dex-volume-mobile-value">{formatMarketCap(token.market_cap_usd)}</span>
                  </div>
                  <div className="dex-volume-mobile-item">
                    <span className="dex-volume-mobile-label">VOL:</span>
                    <span className="dex-volume-mobile-value">{formatVolume(token.volume_usd_24h)}</span>
                  </div>
                </div>
              </td>
              <td>
                {(() => {
                  const change = Number(token.price_change_24h);
                  if (!Number.isFinite(change)) return <span className="dex-change dim">—</span>;
                  const positive = change >= 0;
                  return (
                    <span className={`dex-change ${positive ? "up" : "down"}`}>
                      {positive ? "+" : ""}{change.toFixed(2)}%
                    </span>
                  );
                })()}
              </td>
              <td>
                <span className="dex-volume">{formatVolume(token.volume_usd_24h)}</span>
              </td>
              <td>
                <span className="dex-holders">
                  {Number.isFinite(Number(token.holders)) && Number(token.holders) > 0
                    ? Number(token.holders).toLocaleString()
                    : "—"}
                </span>
              </td>
              <td>
                {token.dex_paid_at ? (
                  <span className="dex-paid-sticker" title="Creator paid for the DexScreener profile">PAID</span>
                ) : (
                  <span className="dex-paid-no">NO</span>
                )}
              </td>
              <td>
                <div className="dex-quick-buy-buttons">
                  <button 
                    className="dex-quick-buy-btn hover-grow-s"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain, '0.2');
                    }}
                    title="Buy 0.2 ETH"
                  >
                    Buy 0.2
                  </button>
                  <button 
                    className="dex-quick-buy-btn hover-grow-s"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain, '0.5');
                    }}
                    title="Buy 0.5 ETH"
                  >
                    Buy 0.5
                  </button>
                  <button 
                    className="dex-quick-buy-btn hover-grow-s"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSnipeBuy(token.contractAddress, token.name, token.symbol, token.chain, '1.0');
                    }}
                    title="Buy 1.0 ETH"
                  >
                    Buy 1.0
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

/**
 * Slurp-style search field with a dropdown of matching tokens. Doesn't
 * filter the grid — just navigates straight to the picked token's page.
 */
const SlurpSearch = React.memo(function SlurpSearch({ tokens, value, onChange }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const matches = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q || !Array.isArray(tokens)) return [];
    const list = [];
    for (const t of tokens) {
      const name = (t?.name || "").toLowerCase();
      const sym = (t?.symbol || "").toLowerCase();
      const ca = (t?.contractAddress || t?.mint || "").toLowerCase();
      if (name.includes(q) || sym.includes(q) || ca.includes(q)) {
        list.push(t);
        if (list.length >= 8) break;
      }
    }
    return list;
  }, [value, tokens]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (t) => {
    setOpen(false);
    onChange?.("");
    navigate(`/token/${t.contractAddress || t.mint}`);
  };

  return (
    <div className="pm-search" ref={wrapRef}>
      <div className="pm-search__field">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="pm-search__icon">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="text"
          className="pm-search__input"
          placeholder="Search by name, ticker, or address"
          value={value || ""}
          onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {value && (
          <button
            type="button"
            className="pm-search__clear"
            onClick={() => { onChange?.(""); setOpen(false); }}
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>
      {open && value && (
        <div className="pm-search__drop">
          {matches.length === 0 ? (
            <div className="pm-search__empty">No tokens match "{value}"</div>
          ) : (
            matches.map((t) => (
              <button
                key={t.contractAddress || t.mint}
                type="button"
                className="pm-search__item"
                onClick={() => pick(t)}
              >
                <span className="pm-search__item-img">
                  {t.image?.url ? (
                    <img src={t.image.url} alt="" />
                  ) : (
                    <span className="pm-search__item-ph" />
                  )}
                </span>
                <span className="pm-search__item-name">{t.name || "Untitled"}</span>
                <span className="pm-search__item-sym">${t.symbol || "—"}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
});

// Memoized SortButtons component to prevent unnecessary re-renders
const SortButtons = React.memo(({ sortField, sortDirection, handleSort }) => {
  const sortOptions = [
    { field: "blockNumber", label: "New" },
    { field: "volume",      label: "Volume" },
    { field: "activity",    label: "Bumps" },
    { field: "marketCap",   label: "MCap" },
  ];

  return (
    <div className="pm-sort">
      {sortOptions.map(({ field, label }) => {
        const isActive = sortField === field;
        const isBumps = field === "activity";
        return (
          <button
            key={field}
            type="button"
            onClick={() => handleSort(field)}
            className={`pm-sort__pill${isActive ? " is-active" : ""}${isBumps ? " pm-sort__pill--bumps" : ""}`}
            aria-pressed={isActive}
          >
            <span className="pm-sort__label">{label}</span>
            {isActive && !isBumps && (
              sortDirection === "desc"
                ? <ArrowDown size={14} className="pm-sort__arrow" aria-hidden="true" />
                : <ArrowUp   size={14} className="pm-sort__arrow" aria-hidden="true" />
            )}
          </button>
        );
      })}
    </div>
  );
});

// Main Token Dashboard Component
function TokenDashboard() {
  // Removed excessive re-render logging

  const { isConnected, emit, addListener, removeListener, silentReconnect } =
    useWebSocket();

  // Wallet connection
  const { address, isConnected: isWalletConnected, chainId } = useAccount();
  const { data: walletClient } = useWalletClient();

  // Live Solana token feed — sole source of truth for every card/table on
  // this page. Seeds from /api/tokens, stays fresh via the /ws stream.
  // Search is server-side; debounced below.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Search drives a navigation dropdown (SlurpSearch) — it does NOT filter
  // the grid. Feed stays unfiltered so all live tokens stay visible while
  // the user types, and they jump straight to the picked token's page.
  const { uiTokens: liveTokensRaw } = useTokenFeed({
    sort: "volume",
    limit: 1000,
  });

  const [tokens, setTokens] = useState([]);
  const [filteredTokens, setFilteredTokens] = useState([]);
  // const [searchQuery, setSearchQuery] = useState("");
  // const [isSearching, setIsSearching] = useState(false);
  // const [searchResults, setSearchResults] = useState([]);
  // const [hasSearched, setHasSearched] = useState(false);
  // const [searchResultsPage, setSearchResultsPage] = useState(1);
  // const [searchResultsTotalPages, setSearchResultsTotalPages] = useState(1);
  
  // Search context
  const {
    searchQuery,
    setSearchQuery,
    isSearching,
    setIsSearching,
    searchResults,
    setSearchResults,
    hasSearched,
    setHasSearched,
    searchResultsPage,
    setSearchResultsPage,
    searchResultsTotalPages,
    setSearchResultsTotalPages
  } = useSearch();

  // Debounce searchQuery → debouncedSearch so we don't hammer /api/tokens on
  // every keystroke. 250ms is snappy enough that the feed feels live.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch((searchQuery || "").trim()), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);
  const [highestMarketCapToken, setHighestMarketCapToken] = useState(() => {
    // Try to load cached highlighted tokens for instant display on navigation back
    try {
      const cached = localStorage.getItem("koa-highlighted-tokens");
      if (cached) {
        const data = JSON.parse(cached);
        // Check if cache is less than 5 minutes old
        if (Date.now() - data.timestamp < 300000) {
          return data.marketCap;
        } else {
          // Cache is stale, remove it
          localStorage.removeItem("koa-highlighted-tokens");
        }
      }
    } catch {
      // Invalid cache, remove it
      localStorage.removeItem("koa-highlighted-tokens");
    }
    return null;
  });
  const [highestVolumeToken, setHighestVolumeToken] = useState(() => {
    // Try to load cached highlighted tokens for instant display on navigation back
    try {
      const cached = localStorage.getItem("koa-highlighted-tokens");
      if (cached) {
        const data = JSON.parse(cached);
        // Check if cache is less than 5 minutes old
        if (Date.now() - data.timestamp < 300000) {
          return data.volume;
        } else {
          // Cache is stale, remove it
          localStorage.removeItem("koa-highlighted-tokens");
        }
      }
    } catch {
      // Invalid cache, remove it
      localStorage.removeItem("koa-highlighted-tokens");
    }
    return null;
  });
  const [loadingTopTokens, setLoadingTopTokens] = useState(
    !highestMarketCapToken || !highestVolumeToken
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ totalTokens: 0, totalVolume: 0 });
  const [sortField, setSortField] = useState("activity");
  const [sortDirection, setSortDirection] = useState("desc");

  // Bumps: buys ≥ 0.05 SOL float the bought token to the front of the grid
  // with a shake animation. Only active on the "Recent Trades" sort, where
  // reordering by recency makes sense. New launches always shake on Newest.
  const [bumpedAt, setBumpedAt] = useState({}); // { mint: { ts, kind } }

  // Client-side sort of the slurp live feed. The bump-to-front pass is
  // applied later in `gridSource` so it works regardless of whether the
  // grid is being fed from liveTokens, filteredTokens, or searchResults.
  const liveTokens = useMemo(() => {
    const list = Array.isArray(liveTokensRaw) ? [...liveTokensRaw] : [];
    const keyOf = (t) => {
      switch (sortField) {
        case "marketCap": return Number(t?.market_cap_usd) || 0;
        case "blockNumber":
        case "newest":    return new Date(t?.created_at || 0).getTime();
        case "activity":  return new Date(t?.last_trade_at || t?.created_at || 0).getTime();
        case "volume":
        default:          return Number(t?.volume_usd_24h) || 0;
      }
    };
    list.sort((a, b) => keyOf(b) - keyOf(a));
    if (sortDirection === "asc") list.reverse();
    return list;
  }, [liveTokensRaw, sortField, sortDirection]);

  // Helper — briefly tag a mint with an animation kind, auto-clear after TTL.
  const addBump = useCallback((mint, kind, ttlMs) => {
    if (!mint) return;
    const ts = Date.now();
    setBumpedAt((prev) => ({ ...prev, [mint]: { ts, kind } }));
    setTimeout(() => {
      setBumpedAt((prev) => {
        const cur = prev[mint];
        if (!cur || cur.ts !== ts) return prev; // newer bump replaced it
        const next = { ...prev };
        delete next[mint];
        return next;
      });
    }, ttlMs);
  }, []);

  // Animation rules — TAB-SCOPED, strict:
  //   • LAUNCHES (token:new) — pop in ONLY on the New tab
  //     (sortField === "blockNumber" or "newest"). New launches go to
  //     position 0 on that tab, so the animation matches the reorder.
  //   • TRADES (trade:new buy) — shake the traded card ONLY on Bumps
  //     (sortField === "activity"). Cards reorder on trade there, so
  //     the shake matches the reorder.
  //   • Every other tab (Volume, MCap) — silent. They're stable ranked
  //     views; animations on them would be confusing noise.
  //   • SELLS — silent everywhere.
  //
  // Read sortField via the ref so the subscription doesn't re-create
  // on every sort change (the closure always sees the live value).
  useEffect(() => {
    if (!slurpWs) return undefined;
    return slurpWs.subscribe((msg) => {
      const currentSort = sortFieldRef.current;
      // "New" UI tab passes "blockNumber" (BNB-era legacy); "newest" is
      // the canonical alias. Both must trigger the launch pop.
      const isNewTab = currentSort === "blockNumber" || currentSort === "newest";
      const isBumpsTab = currentSort === "activity";
      if (msg?.type === "token:new" && isNewTab) {
        addBump(msg.token?.mint, "launch", LAUNCH_SHAKE_MS);
        return;
      }
      if (
        msg?.type === "trade:new" &&
        msg.trade?.mint &&
        msg.trade.side === "buy" &&
        isBumpsTab
      ) {
        addBump(msg.trade.mint, "trade", BUMP_TTL_MS);
      }
    });
  }, [addBump]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [networkFilter] = useState("bsc"); // BSC only

  // Helper function to map network filter to correct chain value
  const getChainValue = (networkFilter) => {
    if (networkFilter === "all") return "all";
    // Use lowercase for better backend compatibility
    if (networkFilter === "base") return "base";
    if (networkFilter === "eth") return "eth";
    if (networkFilter === "bsc") return "bsc";
    return networkFilter;
  };
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Quick Buy states
  const [snipeMode, setSnipeMode] = useState(false);
  const [snipeSettings, setSnipeSettings] = useState(() => {
    const saved = localStorage.getItem('koa-snipe-settings');
    return saved ? JSON.parse(saved) : { defaultEthAmount: '0.1' };
  });
  
  // DEX view state
  const [dexView, setDexView] = useState(false);
  const [showSnipeModal, setShowSnipeModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [networkModalData, setNetworkModalData] = useState({ currentChain: '', expectedChain: '' });

  // Enhanced screen dimensions tracking with height classes
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  // State for token details (social media, age, etc.)
  const [tokenDetails, setTokenDetails] = useState({});
  const [loadingTokenDetails, setLoadingTokenDetails] = useState({});

  //- fetchTokenDetails
  //_ Function: Fetches token details (social media links, etc.) via WebSocket
  //_ Input: tokenAddress - The contract address of the token
  const fetchTokenDetails = useCallback(async (tokenAddress) => {
    if (!tokenAddress) return null;
    
    const cacheKey = tokenAddress.toLowerCase();
    
    // Check if we already have data for this token
    if (tokenDetails[cacheKey]) {
      return tokenDetails[cacheKey];
    }

    // Check if we're already loading this token
    if (loadingTokenDetails[cacheKey]) {
      return null;
    }

    try {
      setLoadingTokenDetails(prev => ({ ...prev, [cacheKey]: true }));
      
      // Use WebSocket to get token details (same as TokenDetailPage)
      // Normalize the token address for comparison
      const normalizedAddress = tokenAddress.toLowerCase();
      
      const wsData = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error('WebSocket request timed out'));
        }, 10000);

        const handleTokenDetails = (data) => {
          // Verify this response is for the correct token address
          const responseAddress = data?.contractAddress?.toLowerCase();
          if (responseAddress !== normalizedAddress) {
            // This response is for a different token, ignore it
            console.log(`⚠️ Ignoring token-details response - expected ${normalizedAddress}, got ${responseAddress}`);
            return;
          }
          
          clearTimeout(timeout);
          cleanup();
          resolve(data);
        };

        const handleError = (error) => {
          clearTimeout(timeout);
          cleanup();
          reject(new Error(error.message || 'Failed to fetch token details'));
        };

        const cleanup = () => {
          removeListener('token-details', handleTokenDetails);
          removeListener('error', handleError);
        };

        // Add listeners
        addListener('token-details', handleTokenDetails);
        addListener('error', handleError);

        // Send request
        emit('get-token-details', { contractAddress: tokenAddress });
      });
      
      // Store the result
      setTokenDetails(prev => ({ ...prev, [cacheKey]: wsData }));
      return wsData;
      
    } catch (error) {
      console.error(`❌ Error fetching token details for ${tokenAddress}:`, error);
      return null;
    } finally {
      setLoadingTokenDetails(prev => ({ ...prev, [cacheKey]: false }));
    }
  }, [tokenDetails, loadingTokenDetails, addListener, removeListener, emit]);

  // Reference to prevent multiple concurrent requests
  const isLoadingRef = useRef(false);
  
  // References to current sort values for network filter effect
  const sortFieldRef = useRef(sortField);
  const sortDirectionRef = useRef(sortDirection);
  
  // Update refs when sort values change
  useEffect(() => {
    sortFieldRef.current = sortField;
    sortDirectionRef.current = sortDirection;
  }, [sortField, sortDirection]);
  const dataTimestampRef = useRef(Date.now());

  // Removed order validation system - injected order should never be overridden

  // State variables for glow effect when first token changes
  const [firstTokenId, setFirstTokenId] = useState(null);
  const [shouldGlowFirstToken, setShouldGlowFirstToken] = useState(false);

  // Track first token changes for glow effect (activity mode only)
  useEffect(() => {
    if (sortField === 'activity' && !hasSearched && filteredTokens.length > 0) {
      const newFirstTokenId = filteredTokens[0].contractAddress;
      
      // If the first token has changed, trigger glow effect
      if (firstTokenId && firstTokenId !== newFirstTokenId) {
        console.log('🌟 [Activity] First token changed from', firstTokenId, 'to', newFirstTokenId);
        setShouldGlowFirstToken(true);
        
        // Remove glow after animation completes
        setTimeout(() => {
          setShouldGlowFirstToken(false);
        }, 1000);
      }
      
      // Update the first token ID
      setFirstTokenId(newFirstTokenId);
    }
  }, [sortField, hasSearched, filteredTokens, firstTokenId]);

  // Debug: Track when highlighted tokens change
  useEffect(() => {
    console.log("🎯 [TokenDashboard] Highlighted tokens state changed:", {
      marketCap: highestMarketCapToken?.name || "null",
      volume: highestVolumeToken?.name || "null",
      loading: loadingTopTokens,
    });

    // Cache highlighted tokens for instant display on navigation back
    if (highestMarketCapToken && highestVolumeToken) {
      try {
        localStorage.setItem(
          "koa-highlighted-tokens",
          JSON.stringify({
            marketCap: highestMarketCapToken,
            volume: highestVolumeToken,
            timestamp: Date.now(),
          })
        );
        console.log(
          "🎯 [TokenDashboard] Cached highlighted tokens for instant navigation"
        );
      } catch (err) {
        console.warn("Failed to cache highlighted tokens:", err);
      }
    }
  }, [highestMarketCapToken, highestVolumeToken, loadingTopTokens]);

  // More granular height breakpoints for 14-inch screens
  const isShortScreen = screenHeight <= 800;
  const isVeryShortScreen = screenHeight <= 700; // Targeting 14-inch screens
  const isExtremelyShortScreen = screenHeight <= 600;

  // Add these refs for managing data loading, viewport updates, and rate limiting
  const lastUpdateTimesRef = useRef({});
  const throttleTimerRef = useRef(null);
  const viewportUpdateTimeoutRef = useRef(null);
  const sortTimeoutRef = useRef(null);

  // Check screen dimensions on resize
  useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Apply more specific height-based classes
  useEffect(() => {
    const appContainer = document.querySelector(".app-container");
    if (appContainer) {
      // Reset all height classes
      appContainer.classList.remove(
        "short-screen",
        "very-short-screen",
        "extremely-short-screen"
      );

      // Apply appropriate class based on current height
      if (isExtremelyShortScreen) {
        appContainer.classList.add("extremely-short-screen");
      } else if (isVeryShortScreen) {
        appContainer.classList.add("very-short-screen");
      } else if (isShortScreen) {
        appContainer.classList.add("short-screen");
      }
    }
  }, [isShortScreen, isVeryShortScreen, isExtremelyShortScreen]);

  // Clear search when query is empty
  useEffect(() => {
    if (!searchQuery.trim() && hasSearched) {
      setHasSearched(false);
      setSearchResults([]);
      setFilteredTokens(tokens);
      setIsSearching(false);
    }
  }, [searchQuery, hasSearched, tokens, setHasSearched, setSearchResults, setIsSearching]);

  // Search functionality is now handled in TopBar component via SearchContext
  
  // Handle search results from WebSocket
  useEffect(() => {
    const searchResultsHandler = (data) => {
      console.log("[TokenDashboard] Search results received:", data);
      setSearchResults(data.tokens || []);
      setSearchResultsTotalPages(data.totalPages || 1);
      setIsSearching(false);
    };

    
    addListener("search-results", searchResultsHandler);
    return () => removeListener("search-results", searchResultsHandler);
  }, [addListener, removeListener, setSearchResults, setSearchResultsTotalPages, setIsSearching]);

  // Handle search results pagination
  const handleSearchPageChange = useCallback(
    (newPage) => {
      if (
        newPage < 1 ||
        newPage > searchResultsTotalPages ||
        isSearching ||
        !hasSearched
      )
        return;

      setSearchResultsPage(newPage);
      setIsSearching(true);

      emit("search-tokens", {
        query: searchQuery.toLowerCase().trim(),
        network: networkFilter === "all" ? undefined : networkFilter,
        page: newPage,
        pageSize: 18,
      });
    },
    [
      // searchResultsPage,
      searchResultsTotalPages,
      isSearching,
      hasSearched,
      emit,
      searchQuery,
      networkFilter,
      setSearchResultsPage,
      setIsSearching,
    ]
  );

  // Simplified - no need for complex token fetching

  // Apply simple sorting when in activity mode  
  useEffect(() => {
    if (!hasSearched) {
      // For activity mode, tokens are already sorted by blockNumber from backend
      setFilteredTokens(tokens);
    }
  }, [tokens, hasSearched, sortField]);

  // Simplified - no need for complex token fetching



  // Rate-limited data fetching to prevent excessive requests
  const fetchTokenData = useCallback(() => {
    // Prevent multiple concurrent data fetches
    if (isLoadingRef.current) return;

    // Add a minimum interval between full data refreshes (5 seconds)
    const now = Date.now();
    if (now - dataTimestampRef.current < 5000 && initialLoadComplete) {
      return;
    }

    // Set loading state
    isLoadingRef.current = true;

    if (!initialLoadComplete) {
      setLoading(true);
    }

    // Update timestamp
    dataTimestampRef.current = now;

    console.log(
      "Fetching tokens with sort:",
      sortField,
      "direction:",
      sortDirection,
      "page:",
      currentPage
    );

    // Apply the same translation as handleSort for consistency
    const sortToRequest = sortField === "activity" ? "activity" : sortField;
    const directionToRequest = sortField === "activity" ? "desc" : sortDirection;
    
    const requestData = {
      sort: sortToRequest,
      direction: directionToRequest,
      page: currentPage,
      chain: getChainValue(networkFilter),
    };
    
    // For activity mode, request standard page size (simplified)
    if (sortField === "activity") {
      console.log('🚀 [Activity] Requesting tokens sorted by most recent trades (fetchTokenData)');
      console.log('🚀 [Activity] Requesting sort:', sortToRequest, 'direction:', directionToRequest);
    }
    
    emit("get-tokens", { ...requestData, clientVersion: 2 });

    // Auto-reset loading after reasonable timeout
    setTimeout(() => {
      isLoadingRef.current = false;
      setLoading(false);
    }, 5000);
  }, [
    sortField,
    sortDirection,
    currentPage,
    emit,
    initialLoadComplete,
    networkFilter,
  ]);

  // Enhanced token injection: fetch missing tokens and inject at top
  const handleNewTrade = (tradeData) => {
    console.log('🔄 [TokenDashboard] New trade detected:', tradeData);
    
    // OPTIMISTIC UPDATE: Immediately inject bought token at top
    if (tradeData && tradeData.type === 'buy' && tradeData.tokenAddress) {
      console.log('💫 [TokenDashboard] Optimistic update: moving bought token to top');
      
      // Find the token in current list
      const currentTokens = filteredTokens.length > 0 ? filteredTokens : tokens;
      const boughtTokenIndex = currentTokens.findIndex(
        token => token.contractAddress?.toLowerCase() === tradeData.tokenAddress?.toLowerCase()
      );
      
      if (boughtTokenIndex > 0) { // Found and not already at top
        const updatedTokens = [...currentTokens];
        const boughtToken = updatedTokens.splice(boughtTokenIndex, 1)[0];
        
        // Add higher last_trade_block for immediate sorting
        const optimisticToken = {
          ...boughtToken,
          last_trade_block: (boughtToken.last_trade_block || 0) + 1,
          _optimistic: true // Mark as optimistic update
        };
        
        updatedTokens.unshift(optimisticToken); // Move to top
        
        console.log(`💫 [TokenDashboard] Moved ${tradeData.token} to position 1 optimistically`);
        
        // Update both token states
        setTokens(updatedTokens);
        setFilteredTokens(updatedTokens);
        
        // FRESH DATA UPDATE: Fetch volume and market cap
        console.log(`🔄 [TokenDashboard] Fetching fresh volume/MC data for ${tradeData.token}`);
        fetchFreshTokenData(tradeData.tokenAddress, boughtToken.chain).then(freshData => {
          if (freshData) {
            console.log(`✅ [TokenDashboard] Applying fresh data to ${tradeData.token}`);
            
            // Update the token with fresh data
            const updateTokenWithFreshData = (tokensList) => {
              return tokensList.map(token => {
                if (token.contractAddress?.toLowerCase() === tradeData.tokenAddress?.toLowerCase()) {
                  return {
                    ...token,
                    volume_usd_24h: freshData.volume_usd_24h,
                    market_cap_usd: freshData.market_cap_usd,
                    _freshDataApplied: true
                  };
                }
                return token;
              });
            };
            
            setTokens(updateTokenWithFreshData);
            setFilteredTokens(updateTokenWithFreshData);
            
            console.log(`✅ [TokenDashboard] Fresh data applied to ${tradeData.token}`);
          } else {
            console.log(`ℹ️ [TokenDashboard] No fresh data available for ${tradeData.token} - using existing data`);
          }
        });
        
      } else if (boughtTokenIndex === -1) {
        console.log(`💫 [TokenDashboard] Token ${tradeData.token} not found in current page - FETCHING FROM DATABASE`);
        
        // TOKEN NOT FOUND - FETCH IT FROM DATABASE AND INJECT
        if (isConnected) {
          console.log(`🔍 [TokenDashboard] Requesting token ${tradeData.tokenAddress} from database`);
          emit("get-single-token", { contractAddress: tradeData.tokenAddress });
        }
      } else {
        console.log(`💫 [TokenDashboard] Token ${tradeData.token} already at top`);
        
        // Token already at top, but still fetch fresh data
        const existingToken = currentTokens[0];
        console.log(`🔄 [TokenDashboard] Fetching fresh volume/MC data for already-top token ${tradeData.token}`);
        fetchFreshTokenData(tradeData.tokenAddress, existingToken.chain).then(freshData => {
          if (freshData) {
            console.log(`✅ [TokenDashboard] Applying fresh data to top token ${tradeData.token}`);
            
            // Update the token with fresh data
            const updateTokenWithFreshData = (tokensList) => {
              return tokensList.map(token => {
                if (token.contractAddress?.toLowerCase() === tradeData.tokenAddress?.toLowerCase()) {
                  return {
                    ...token,
                    volume_usd_24h: freshData.volume_usd_24h,
                    market_cap_usd: freshData.market_cap_usd,
                    _freshDataApplied: true
                  };
                }
                return token;
              });
            };
            
            setTokens(updateTokenWithFreshData);
            setFilteredTokens(updateTokenWithFreshData);
            
            console.log(`✅ [TokenDashboard] Fresh data applied to top token ${tradeData.token}`);
          } else {
            console.log(`ℹ️ [TokenDashboard] No fresh data available for top token ${tradeData.token} - using existing data`);
          }
        });
      }
    }
    
    // Removed SMART REFRESH logic - injected order should never be overridden by database updates
    console.log(`✅ [TokenDashboard] Token injection complete - maintaining injected order`);
    
    // No database validation or refresh - the injected order from singleTokenResultHandler is final
  };

  // Only fetch data when page changes, not when sort changes (handleSort now handles that)
  useEffect(() => {
    if (isConnected && !isSearching && initialLoadComplete) {
      // Only trigger for page changes, not sort changes
      // Clear any existing throttle timer
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }

      // Add small delay to prevent rapid requests during multiple state changes
      throttleTimerRef.current = setTimeout(() => {
        // Only fetch if this is specifically a page change (not a sort change)
        const now = Date.now();
        if (now - dataTimestampRef.current > 1000) {
          // Minimum 1 second between fetches
          fetchTokenData();
        }
      }, 200);
    }

    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
    };
  }, [
    isConnected,
    currentPage,
    isSearching,
    fetchTokenData,
    initialLoadComplete,
  ]);

  // Handle network filter changes - fetch new data when filter changes
  useEffect(() => {
    if (isConnected && !isSearching) {
      console.log(
        `[TokenDashboard] Network filter changed to: ${networkFilter}, fetching new data`
      );

      // Reset to first page when changing network filter
      setCurrentPage(1);

      // Apply the same translation as handleSort for consistency using refs
      const sortToRequest = sortFieldRef.current === "activity" ? "activity" : sortFieldRef.current;
      const directionToRequest = sortFieldRef.current === "activity" ? "desc" : sortDirectionRef.current;
      
      const requestData = {
        sort: sortToRequest,
        direction: directionToRequest,
        page: 1,
        chain: getChainValue(networkFilter),
      };
      
      // For activity mode, request standard page size (simplified)
      if (sortFieldRef.current === "activity") {
        console.log('🚀 [Activity] Network filter change - requesting tokens for activity mode');
      }
      
      emit("get-tokens", { ...requestData, clientVersion: 2 });
    }
  }, [networkFilter, isConnected, isSearching, emit]); // Removed sortField and sortDirection to prevent double fetching

  // Add this function to check if a token is in viewport
  const isTokenInViewport = useCallback((tokenElement) => {
    if (!tokenElement) return false;
    const rect = tokenElement.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }, []);

  // Define handleViewportUpdate function at component level
  const handleViewportUpdate = useCallback(() => {
    // Throttle viewport updates to prevent excessive messages
    if (viewportUpdateTimeoutRef.current) {
      clearTimeout(viewportUpdateTimeoutRef.current);
    }

    viewportUpdateTimeoutRef.current = setTimeout(() => {
      const visibleTokens = Array.from(
        document.querySelectorAll(".token-card-new")
      )
        .filter((card) => isTokenInViewport(card))
        .map((card) => card.dataset.tokenAddress)
        .filter(Boolean); // Filter out any undefined values

      if (visibleTokens.length > 0) {
        emit("viewport-tokens", visibleTokens);
      }
    }, 300); // 300ms throttle
  }, [isTokenInViewport, emit]);

  // Single token injection handlers for bump mode
  const singleTokenResultHandler = useCallback((data) => {
    try {
      console.log('🎯 [TokenDashboard] Single token result received:', data);
      
      if (data && data.token && sortField === 'activity') {
        const fetchedToken = {
          ...data.token,
          last_trade_block: Date.now(), // Give it the highest block number for sorting
          _injected: true // Mark as injected token
        };
        
        console.log(`🚀 [TokenDashboard] Injecting token ${fetchedToken.symbol} at top of list`);
        
        // Update both token states - move token to top (bump existing tokens down)
        setTokens(currentTokens => {
          const tokensToUse = filteredTokens.length > 0 ? filteredTokens : currentTokens;
          
          // Check if token already exists 
          const existingIndex = tokensToUse.findIndex(
            token => token.contractAddress?.toLowerCase() === fetchedToken.contractAddress?.toLowerCase()
          );
          
          if (existingIndex !== -1) {
            // Token exists - move it to the front and bump others down
            console.log(`🎯 [TokenDashboard] Token ${fetchedToken.symbol} exists at position ${existingIndex}, moving to front`);
            const updatedTokens = [...tokensToUse];
            updatedTokens.splice(existingIndex, 1); // Remove from current position
            updatedTokens.unshift(fetchedToken); // Add to front
            return updatedTokens;
          } else {
            // Token doesn't exist - add at top and remove last
            console.log(`🎯 [TokenDashboard] Injecting new token ${fetchedToken.symbol} at top of tokens list`);
            return [fetchedToken, ...tokensToUse.slice(0, -1)]; // Add at top, remove last
          }
        });
        
        setFilteredTokens(currentFiltered => {
          const tokensToUse = currentFiltered.length > 0 ? currentFiltered : tokens;
          
          // Check if token already exists 
          const existingIndex = tokensToUse.findIndex(
            token => token.contractAddress?.toLowerCase() === fetchedToken.contractAddress?.toLowerCase()
          );
          
          if (existingIndex !== -1) {
            // Token exists - move it to the front and bump others down
            console.log(`🎯 [TokenDashboard] Token ${fetchedToken.symbol} exists in filtered tokens at position ${existingIndex}, moving to front`);
            const updatedTokens = [...tokensToUse];
            updatedTokens.splice(existingIndex, 1); // Remove from current position
            updatedTokens.unshift(fetchedToken); // Add to front
            return updatedTokens;
          } else {
            // Token doesn't exist - add at top and remove last
            console.log(`🎯 [TokenDashboard] Injecting new token ${fetchedToken.symbol} at top of filtered tokens list`);
            return [fetchedToken, ...tokensToUse.slice(0, -1)]; // Add at top, remove last
          }
        });
        
        // FRESH DATA UPDATE: Fetch volume and market cap for injected token
        console.log(`🔄 [TokenDashboard] Fetching fresh volume/MC data for injected token ${fetchedToken.symbol}`);
        fetchFreshTokenData(fetchedToken.contractAddress, fetchedToken.chain).then(freshData => {
          if (freshData) {
            console.log(`✅ [TokenDashboard] Applying fresh data to injected token ${fetchedToken.symbol}`);
            
            // Update the token with fresh data
            const updateTokenWithFreshData = (tokensList) => {
              return tokensList.map(token => {
                if (token.contractAddress?.toLowerCase() === fetchedToken.contractAddress?.toLowerCase()) {
                  return {
                    ...token,
                    volume_usd_24h: freshData.volume_usd_24h,
                    market_cap_usd: freshData.market_cap_usd,
                    _freshDataApplied: true
                  };
                }
                return token;
              });
            };
            
            setTokens(updateTokenWithFreshData);
            setFilteredTokens(updateTokenWithFreshData);
            
            console.log(`✅ [TokenDashboard] Fresh data applied to injected token ${fetchedToken.symbol}`);
          } else {
            console.log(`ℹ️ [TokenDashboard] No fresh data available for injected token ${fetchedToken.symbol} - using existing data`);
          }
        });
      }
    } catch (error) {
      console.error('Error processing single token result:', error);
    }
  }, [sortField, filteredTokens, tokens]);
  
  const singleTokenNotFoundHandler = useCallback((data) => {
    console.log('❌ [TokenDashboard] Single token not found:', data);
    // Token doesn't exist in database - this is expected for some trades
  }, []);

  // Improved token update handler with rate limiting and better error handling
  const tokenUpdateHandler = useCallback(
    (updates) => {
      try {
        if (!updates || !Array.isArray(updates) || updates.length === 0) {
          return;
        }

        // Group updates by timestamp to avoid processing too many batches
        const now = Date.now();

        // Process updates in batches to prevent UI jank
        const processableUpdates = updates.filter((update) => {
          if (!update || !update.contractAddress) return false;

          // Rate limit per token
          const lastUpdate =
            lastUpdateTimesRef.current[update.contractAddress] || 0;
          // Allow update if more than 1.5 seconds passed since last update for this token
          if (now - lastUpdate > 1500) {
            lastUpdateTimesRef.current[update.contractAddress] = now;
            return true;
          }
          return false;
        });

        if (processableUpdates.length === 0) {
          return;
        }

        // Use React.startTransition to mark these as non-urgent updates
        React.startTransition(() => {
          // Process all updates in a single state update to prevent multiple renders
          setTokens((currentTokens) => {
            const contractAddressToIndex = new Map();
            currentTokens.forEach((token, index) => {
              contractAddressToIndex.set(token.contractAddress, index);
            });

            const updatedTokens = [...currentTokens];
            let hasChanges = false;

            processableUpdates.forEach((updatedToken) => {
              const index = contractAddressToIndex.get(
                updatedToken.contractAddress
              );

              if (index !== undefined) {
                // Update existing token
                updatedTokens[index] = {
                  ...updatedTokens[index],
                  ...updatedToken,
                };
                hasChanges = true;
              }
            });

            return hasChanges ? updatedTokens : currentTokens;
          });

          // Update filtered tokens in a single batch only if not searching
          if (!isSearching && !hasSearched) {
            setFilteredTokens((currentFilteredTokens) => {
              const contractAddressToIndex = new Map();
              currentFilteredTokens.forEach((token, index) => {
                contractAddressToIndex.set(token.contractAddress, index);
              });

              const updatedFilteredTokens = [...currentFilteredTokens];
              let hasChanges = false;

              processableUpdates.forEach((updatedToken) => {
                const index = contractAddressToIndex.get(
                  updatedToken.contractAddress
                );

                if (index !== undefined) {
                  // Update existing token
                  updatedFilteredTokens[index] = {
                    ...updatedFilteredTokens[index],
                    ...updatedToken,
                  };
                  hasChanges = true;
                }
              });

              return hasChanges ? updatedFilteredTokens : currentFilteredTokens;
            });
          }

          // Update the highlighted tokens if needed
          processableUpdates.forEach((updatedToken) => {
            if (
              highestMarketCapToken?.contractAddress ===
              updatedToken.contractAddress
            ) {
              setHighestMarketCapToken((prev) => ({
                ...prev,
                ...updatedToken,
              }));
            }
            if (
              highestVolumeToken?.contractAddress ===
              updatedToken.contractAddress
            ) {
              setHighestVolumeToken((prev) => ({ ...prev, ...updatedToken }));
            }
          });
        });
      } catch (err) {
        console.error("Error processing token updates:", err);
      }
    },
    [
      isSearching,
      hasSearched,
      highestMarketCapToken?.contractAddress,
      highestVolumeToken?.contractAddress,
    ]
  );

  // Optimized handleSort function with debouncing
  const handleSort = useCallback(
    (field) => {
      // Handle Quick Buy mode toggle
      if (field === "snipe") {
        if (snipeMode) {
          // Turn off Quick Buy mode
          setSnipeMode(false);
          console.log("🎯 Quick Buy mode turned OFF");
        } else {
          // Turn on Quick Buy mode - show settings modal
          setShowSnipeModal(true);
          console.log("🎯 Quick Buy mode settings opened");
        }
        return;
      }

      if (field === "dex") {
        setDexView(true);
        return;
      }

      if (field === "normal") {
        setDexView(false);
        return;
      }

  
      // if (field === "dex") {
      //   setDexView(prev => {
      //     const next = !prev;
      //     // alert(next);
      //     console.log(`📊 DEX view ${next ? 'enabled' : 'disabled'}`);
      //     return next;
      //   });
      //   return;
      // }

      const newDirection =
        sortField === field && sortDirection === "desc" ? "asc" : "desc";
      setSortField(field);
      setSortDirection(newDirection);
      setCurrentPage(1); // Reset to first page

      console.log(
        `Sorting by ${field} in ${newDirection} order with network filter: ${networkFilter}`
      );

            // Use React.startTransition for immediate UI feedback
      React.startTransition(() => {
        if (isConnected && !isSearching) {
          // For activity mode, request tokens sorted by activity (blockNumber desc)
          const sortToRequest = field === "activity" ? "activity" : field;
          const directionToRequest = field === "activity" ? "desc" : newDirection;
          
          emit("get-tokens", {
            sort: sortToRequest,
            direction: directionToRequest,
            page: 1,
            chain: getChainValue(networkFilter),
            clientVersion: 2,
          });
        }
      });
    },
    [sortField, sortDirection, isConnected, isSearching, emit, networkFilter, snipeMode]
  );

  // Add effect to handle cache expiration and request fresh data
  useEffect(() => {
    // If we're showing loading state for top tokens but have an active connection
    if (loadingTopTokens && isConnected && (!highestMarketCapToken || !highestVolumeToken)) {
      console.log("🎯 [TokenDashboard] Top tokens cache expired, requesting fresh token data");
      // Request regular tokens which will extract top tokens automatically
      // Use current sort field instead of hardcoding "volume"
      const sortToRequest = sortField === "activity" ? "activity" : sortField;
      const directionToRequest = sortField === "activity" ? "desc" : sortDirection;
      
      emit("get-tokens", {
        sort: sortToRequest,
        direction: directionToRequest,
        page: currentPage,
        chain: getChainValue(networkFilter),
        clientVersion: 2,
      });
    }
  }, [loadingTopTokens, isConnected, highestMarketCapToken, highestVolumeToken, emit, networkFilter, sortField, sortDirection, currentPage]);

  // Add periodic cache expiration check for top tokens
  useEffect(() => {
    if (!isConnected) return;

    const checkCacheExpiration = () => {
      try {
        const cached = localStorage.getItem("koa-highlighted-tokens");
        if (cached) {
          const data = JSON.parse(cached);
          // Check if cache is expired (older than 5 minutes)
          if (Date.now() - data.timestamp >= 300000) {
            console.log("🎯 [TokenDashboard] Cache expired, clearing and requesting fresh tokens");
            localStorage.removeItem("koa-highlighted-tokens");
            setHighestMarketCapToken(null);
            setHighestVolumeToken(null);
            setLoadingTopTokens(true);
            // Request regular tokens which will extract top tokens automatically
            // Use current sort field instead of hardcoding "volume"
            const sortToRequest = sortField === "activity" ? "activity" : sortField;
            const directionToRequest = sortField === "activity" ? "desc" : sortDirection;
            
            emit("get-tokens", {
              sort: sortToRequest,
              direction: directionToRequest,
              page: currentPage,
              chain: getChainValue(networkFilter),
              clientVersion: 2,
            });
          }
                  } else if (highestMarketCapToken && highestVolumeToken) {
            // If we have tokens but no cache, the cache was cleared - request fresh ones
            console.log("🎯 [TokenDashboard] Cache missing, requesting fresh tokens");
            setLoadingTopTokens(true);
            // Request regular tokens which will extract top tokens automatically
            // Use current sort field instead of hardcoding "volume"
            const sortToRequest = sortField === "activity" ? "activity" : sortField;
            const directionToRequest = sortField === "activity" ? "desc" : sortDirection;
            
            emit("get-tokens", {
              sort: sortToRequest,
              direction: directionToRequest,
              page: currentPage,
              chain: getChainValue(networkFilter),
              clientVersion: 2,
            });
          }
      } catch (error) {
        console.error("Error checking cache expiration:", error);
      }
    };

    // Check immediately and then every 30 seconds
    checkCacheExpiration();
    const cacheCheckInterval = setInterval(checkCacheExpiration, 30000);

    return () => clearInterval(cacheCheckInterval);
  }, [isConnected, highestMarketCapToken, highestVolumeToken, emit, networkFilter, sortField, sortDirection, currentPage]);



  // Handle missing token fetching - DEFINED EARLY TO AVOID HOISTING ISSUES


  // Simplified - no need for complex sorting function

  // Simplified - no need for complex event handlers

  // Setup event listeners for WebSocket with improved error handling
  useEffect(() => {
    // Removed excessive debug logging
    if (isConnected) {
      // Register token list update listener with better error handling
      const tokensListUpdateHandler = (data) => {
        try {
          if (!data || !Array.isArray(data.tokens)) {
            console.error("Invalid tokens data received:", data);
            return;
          }

          // 🚀 OPTIMIZATION 2: Handle minimal payload format for activity mode
          let processedTokens = data.tokens;
          if (data.tokens && data.tokens.length > 0 && data.tokens[0].n) {
            // Convert minimal format back to standard format
            processedTokens = data.tokens.map(token => ({
              name: token.n,
              symbol: token.s,
              price_usd: token.p,
              market_cap_usd: token.m,
              volume_usd_24h: token.v,
              contractAddress: token.a,
              chain: token.c,
              last_trade_block: token.l,
              blockNumber: token.b,
              image: { url: token.i }
            }));
            console.log("⚡ [TokenDashboard] Converted minimal payload to standard format");
          }
          
          // DEBUG: Log activity mode token updates
          if (sortField === 'activity' && processedTokens.length > 0) {
            console.log('🔄 [TokenDashboard] Activity mode tokens received - new order:');
            processedTokens.slice(0, 5).forEach((token, idx) => {
              console.log(`  ${idx + 1}. ${token.symbol} - Block: ${token.last_trade_block}`);
            });
          }

          const filteredTokens = processedTokens.filter(
            (token) => token.symbol !== "WETH" && token.symbol !== "UNI-V3-POS"
          );

          // Removed excessive network/chain debug logging

          // Add timestamp to force React to detect changes
          const tokensWithTimestamp = filteredTokens.map(token => ({
            ...token,
            _lastUpdated: Date.now()
          }));
          
          // BULLETPROOF FIX: Save social/age data BEFORE replacing tokens
          // Debug: Saving social/age data before sort
          
          // Get current tokens and save their social/age data
          const currentTokens = tokens; // Get current tokens from state
          const socialAgeData = new Map();
          
          // Debug: Current tokens before saving social/age data
          currentTokens.forEach((token, index) => {
            // Debug: Token data logging removed for cleaner console
            
            if (token.contractAddress) {
              socialAgeData.set(token.contractAddress.toLowerCase(), {
                website: token.website,
                telegram: token.telegram,
                twitter: token.twitter,
                createdAt: token.createdAt
              });
            }
          });
          
          // Debug: Saved social/age data for tokens
          
          // Now merge the new sorted data with saved social/age data
          const mergedTokens = tokensWithTimestamp.map(newToken => {
            const savedData = socialAgeData.get(newToken.contractAddress?.toLowerCase());
            
            if (savedData) {
              // Debug: Restoring social/age data for token
              return {
                ...newToken,
                ...savedData, // Restore social/age fields
                _lastUpdated: Date.now()
              };
            }
            
            return newToken;
          });
          
          // Debug: Final merged tokens with social/age data
          setTokens(mergedTokens);
          // Only set filtered tokens if there's no active search
          if (!isSearching && !hasSearched) {
            // Use the same merged tokens for filtered tokens
            setFilteredTokens(mergedTokens);
          }
          
          // DEBUG: Force re-render for activity mode
          if (sortField === 'activity') {
            console.log('🔄 [TokenDashboard] Activity mode: forcing filteredTokens update');
            setFilteredTokens([...tokensWithTimestamp]); // Force new array reference
            
            // Additional debug: Log first token address
            if (tokensWithTimestamp.length > 0) {
              console.log('🔄 [TokenDashboard] New first token address:', tokensWithTimestamp[0].contractAddress);
            }
          }

          if (data.totalPages) {
            setTotalPages(data.totalPages);
          }

          // Extract top tokens from regular token data if we don't have them or need to refresh
          // ALWAYS extract when sorting by marketCap or volume to ensure accurate hero positions
          if (filteredTokens.length > 0 && (loadingTopTokens || !highestMarketCapToken || !highestVolumeToken || sortField === 'marketCap' || sortField === 'volume')) {
            // Find highest market cap token
            const topMarketCapToken = filteredTokens.reduce((max, token) => {
              return (token.market_cap_usd || 0) > (max.market_cap_usd || 0) ? token : max;
            }, filteredTokens[0]);

            // Find highest volume token
            const topVolumeToken = filteredTokens.reduce((max, token) => {
              return (token.volume_usd_24h || 0) > (max.volume_usd_24h || 0) ? token : max;
            }, filteredTokens[0]);

            if (topMarketCapToken && topVolumeToken) {
              console.log(`🎯 [TokenDashboard] Extracted top tokens from regular data: ${topMarketCapToken.name} (Market Cap: $${(topMarketCapToken.market_cap_usd || 0).toLocaleString()}), ${topVolumeToken.name} (Volume: $${(topVolumeToken.volume_usd_24h || 0).toLocaleString()})`);
              
              setHighestMarketCapToken(topMarketCapToken);
              setHighestVolumeToken(topVolumeToken);
              setLoadingTopTokens(false);
            }
          }

          // Mark initial load as complete once we have data
          if (!initialLoadComplete && filteredTokens.length > 0) {
            setInitialLoadComplete(true);
          }

          setLoading(false);
          isLoadingRef.current = false;

        } catch (err) {
          console.error("Error processing tokens list update:", err);
          setError("Error processing token data");
        }
      };

      // Register search results handler
      const searchResultsHandler = (data) => {
        try {
          if (!data || !Array.isArray(data.tokens)) {
            console.error("Invalid search results received:", data);
            return;
          }

          setSearchResults(data.tokens);
          setSearchResultsPage(data.currentPage || 1);
          setSearchResultsTotalPages(data.totalPages || 1);
          setIsSearching(false);
        } catch (err) {
          console.error("Error processing search results:", err);
          setError("Error processing search results");
        }
      };



      // Top tokens are now extracted from regular token data automatically



      // Improved fallback mechanism: adaptive timeout based on connection status
      const loadingFallbackTimer = setTimeout(
        () => {
          if (!highestMarketCapToken || !highestVolumeToken) {
            console.log(
              "[TokenDashboard] No instant tokens received, showing loading state"
            );
            setLoadingTopTokens(true);
          }
        },
        isConnected ? 3000 : 1000
      ); // Longer timeout if already connected (navigation back), shorter if connecting fresh

      // Register error handler with graceful degradation
      const errorHandler = (errorData) => {
        console.error("[TokenDashboard] Socket error:", errorData);
        setError(`Error: ${errorData?.message || "Unknown error"}`);
        // Try to reconnect silently on error, don't refresh the page
        if (silentReconnect) {
          setTimeout(silentReconnect, 5000);
        }
      };

      // Set up viewport update listener with passive option for better performance
      window.addEventListener("scroll", handleViewportUpdate, {
        passive: true,
      });
      window.addEventListener("resize", handleViewportUpdate, {
        passive: true,
      });

      // Initial viewport update with delay to ensure DOM is ready
      setTimeout(handleViewportUpdate, 1000);

      // Add all event listeners
      addListener("tokens-list-update", tokensListUpdateHandler);
      addListener("token-updates", tokenUpdateHandler);
      addListener("search-results", searchResultsHandler);
      addListener("single-token-result", singleTokenResultHandler);
      addListener("single-token-not-found", singleTokenNotFoundHandler);
      addListener("error", errorHandler);

      // Request initial data - optimized for navigation back experience
      if (!isSearching && !initialLoadComplete) {
        // Try to request initial tokens data with fallback
        console.log("Requesting initial tokens data...");

        // HTTP fallback function
        const attemptHttpFallback = async () => {
          try {
            // Try to get tokens data from a working endpoint
            // Since global stats works, let's try to get the data structure from there
            const response = await axios.get(
              "https://bnb-pad-backend.onrender.com/api/global-stats"
            );
            if (response.data) {
              // At least set some basic data to show the app is working
              setError(
                "Displaying global stats. Token list temporarily unavailable."
              );
              setStats(response.data);
              setInitialLoadComplete(true);
            } else {
              setError(
                "Unable to load data. The API may be temporarily unavailable."
              );
            }
          } catch (httpError) {
            console.error("HTTP fallback also failed:", httpError);
            setError(
              "Unable to load data. The API may be temporarily unavailable."
            );
          } finally {
            setLoading(false);
            isLoadingRef.current = false;
          }
        };

        if (isConnected) {
          // Try WebSocket first - faster timeout for better navigation experience
          try {
                    emit("get-tokens", {
          sort: sortField,
          direction: sortDirection,
          page: currentPage,
          chain: getChainValue(networkFilter),
          clientVersion: 2,
        });

            // Top tokens will be extracted automatically from regular token data

            // Shorter timeout for better navigation back experience
            setTimeout(() => {
              if (isLoadingRef.current && tokens.length === 0) {
                console.log("WebSocket data failed, trying HTTP fallback...");
                attemptHttpFallback();
              }
            }, 3000); // Reduced from 5 seconds to 3 seconds
          } catch (error) {
            console.error("Error emitting get-tokens:", error);
            attemptHttpFallback();
          }
        } else {
          // WebSocket not connected, try HTTP fallback immediately
          attemptHttpFallback();
        }
      }

      // Cleanup function
      return () => {
        // Removed excessive debug logging
        if (viewportUpdateTimeoutRef.current) {
          clearTimeout(viewportUpdateTimeoutRef.current);
        }

        // Clear fallback timer
        clearTimeout(loadingFallbackTimer);

        window.removeEventListener("scroll", handleViewportUpdate);
        window.removeEventListener("resize", handleViewportUpdate);
        removeListener("tokens-list-update", tokensListUpdateHandler);
        removeListener("token-updates", tokenUpdateHandler);
        removeListener("search-results", searchResultsHandler);
        removeListener("single-token-result", singleTokenResultHandler);
        removeListener("single-token-not-found", singleTokenNotFoundHandler);
        removeListener("error", errorHandler);
      };
    } else {
      // WebSocket not connected, skipping setup
    }
  }, [
    isConnected,
    isSearching,
    isTokenInViewport,
    emit,
    addListener,
    removeListener,
    silentReconnect,
    fetchTokenData,
    initialLoadComplete,
    handleViewportUpdate,
    tokenUpdateHandler,
    singleTokenResultHandler,
    singleTokenNotFoundHandler,
    sortField,
    sortDirection,
    currentPage,
    networkFilter,
    // Removed pendingOrderValidation dependency since it no longer exists
  ]);

  // Simplified - no need for complex single-token event handling
  // Removed excessive debug logging

  // Removed periodic refresh for activity mode to improve performance
  // Activity mode updates are now triggered only by real trading activity via StreamingBanner

  // Cleanup sort timeout on unmount
  useEffect(() => {
    return () => {
      if (sortTimeoutRef.current) {
        clearTimeout(sortTimeoutRef.current);
      }
    };
  }, []);

  // Handle snipe buy clicks - execute swap directly without navigation
  const handleSnipeBuy = useCallback(async (tokenAddress, tokenName, tokenSymbol, tokenChain, customAmount = null) => {
    let buyAmount = customAmount || snipeSettings.defaultEthAmount;
    
    // Ensure buyAmount is a string (ethers.parseEther requires string)
    if (typeof buyAmount !== 'string') {
      buyAmount = buyAmount.toString();
    }
    
    console.log(`🎯 Snipe buy triggered for ${tokenName} (${tokenSymbol}) - ${tokenAddress}`);
    console.log(`💰 Buy amount: ${buyAmount} ETH`);
    console.log(`🌐 Current chain ID: ${chainId}, Expected: ${getChainId(tokenChain)}`);
    
    // Check wallet connection
    if (!isWalletConnected || !address) {
      alert('🔐 Please connect your wallet first to use Quick Buy!');
      return;
    }

    // Check network compatibility
    const expectedChainId = getChainId(tokenChain);
    if (chainId !== parseInt(expectedChainId)) {
      const chainNames = {
        '1': 'Ethereum',
        '8453': 'Base',
        '56': 'BSC'
      };
      const currentChain = chainNames[chainId?.toString()] || `Chain ID ${chainId}`;
      const expectedChain = chainNames[expectedChainId] || `Chain ID ${expectedChainId}`;
      
      setNetworkModalData({ currentChain, expectedChain });
      setShowNetworkModal(true);
      return;
    }

    try {
      console.log('🎯 Executing direct snipe buy immediately...');
      
      // Set up ethers provider from wallet client
      let provider, signer;
      if (walletClient) {
        provider = new ethers.BrowserProvider(walletClient);
        signer = await provider.getSigner();
      } else {
        throw new Error('Wallet client not available');
      }

      // Prepare swap parameters
      const chain = tokenChain || 'Base';
      const sellToken = get0xTokenAddress(chain, true); // Native ETH
      const buyToken = tokenAddress; // Token contract address
      const sellAmount = ethers.parseEther(buyAmount);

      console.log(`🎯 Getting 0x quote for ${buyAmount} ETH -> ${tokenSymbol}`);
      
      // Get quote from 0x
      const quote = await get0xQuote(chain, sellToken, buyToken, sellAmount, address);
      console.log('🎯 0x Quote received:', quote);

      // Get swap transaction data
      const swapData = await get0xSwap(chain, sellToken, buyToken, sellAmount, address);
      console.log('🎯 0x Swap data received:', swapData);

      // Execute the transaction
      console.log('🎯 Executing swap transaction...');
      const tx = await signer.sendTransaction({
        to: swapData.transaction.to,
        data: swapData.transaction.data,
        value: swapData.transaction.value || "0",
        gasLimit: swapData.transaction.gas || "2000000",
        gasPrice: swapData.transaction.gasPrice || undefined,
      });

      console.log('🎯 Snipe buy transaction sent:', tx.hash);
      alert(`🎯 SNIPE BUY EXECUTED!\n\nTransaction Hash: ${tx.hash}\n\nWaiting for confirmation...`);

      // Wait for confirmation
      const receipt = await tx.wait();
      console.log('🎯 Snipe buy confirmed:', receipt);
      
      alert(`✅ SNIPE BUY SUCCESSFUL!\n\nPurchased ${tokenSymbol} for ${buyAmount} ETH\n\nTransaction confirmed in block ${receipt.blockNumber}`);
      
    } catch (error) {
      console.error('🎯 Snipe buy error:', error);
      let errorMessage = 'Snipe buy failed: ';
      
      if (error.message.includes('user rejected')) {
        errorMessage += 'Transaction was rejected by user';
      } else if (error.message.includes('insufficient funds')) {
        errorMessage += 'Insufficient funds for transaction and gas fees';
      } else if (error.message.includes('0x API error')) {
        errorMessage += error.message;
      } else {
        errorMessage += error.message || 'Unknown error occurred';
      }
      
      alert(`❌ ${errorMessage}`);
    }
  }, [snipeSettings.defaultEthAmount, isWalletConnected, address, walletClient, chainId]);

  // Client-side pagination over the live feed. The legacy backend pagination
  // is bypassed because useTokenFeed already pulls a deep window (limit:200);
  // we just slice it into TOKENS_PER_PAGE chunks for the grid. The bump
  // float pass also lives here so it applies whether the source is the
  // legacy filteredTokens stream or the new liveTokens feed.
  // The slurp backend is the ONLY source of truth — the Recent Trades sort
  // already reorders by last_trade_at, so no manual float is needed here.
  // `bumpedAt` only drives the shake/flash animation classes.
  const gridSource = useMemo(() => {
    if (hasSearched) return Array.isArray(searchResults) ? searchResults : [];
    return liveTokens;
  }, [hasSearched, searchResults, liveTokens]);

  const displayedTotalPages = Math.max(1, Math.ceil(gridSource.length / TOKENS_PER_PAGE));
  const safePage = Math.min(Math.max(1, currentPage), displayedTotalPages);
  useEffect(() => {
    if (safePage !== currentPage) setCurrentPage(safePage);
  }, [safePage, currentPage]);

  const pagedTokens = useMemo(() => {
    const start = (safePage - 1) * TOKENS_PER_PAGE;
    return gridSource.slice(start, start + TOKENS_PER_PAGE);
  }, [gridSource, safePage]);
  // Loading-overlay branch shows the bare liveTokens (pre-search/filter).
  const pagedLiveTokens = useMemo(() => {
    const start = (safePage - 1) * TOKENS_PER_PAGE;
    return liveTokens.slice(start, start + TOKENS_PER_PAGE);
  }, [liveTokens, safePage]);

  // Recent Trades already reorders by last_trade_at, so whatever just landed
  // in slot 0 IS the freshly-bumped token — shake it. No separate trade
  // subscription needed.
  const prevTopMintRef = useRef(null);
  useEffect(() => {
    if (sortField !== "activity") {
      prevTopMintRef.current = pagedTokens[0]?.contractAddress || pagedTokens[0]?.mint || null;
      return;
    }
    const topMint = pagedTokens[0]?.contractAddress || pagedTokens[0]?.mint || null;
    const prevTop = prevTopMintRef.current;
    if (topMint && prevTop && topMint !== prevTop) {
      addBump(topMint, "trade", BUMP_TTL_MS);
    }
    prevTopMintRef.current = topMint;
  }, [pagedTokens, sortField, addBump]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > displayedTotalPages || isSearching) return;

    setCurrentPage(newPage);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    console.log(
      `Changing to page ${newPage} with network filter: ${networkFilter}`
    );

    // Request new page data
    if (isConnected) {
          // Apply the same translation as handleSort for consistency
    const sortToRequest = sortField === "activity" ? "activity" : sortField;
    const directionToRequest = sortField === "activity" ? "desc" : sortDirection;
    
    const requestData = {
      sort: sortToRequest,
      direction: directionToRequest,
      page: newPage,
      chain: getChainValue(networkFilter),
    };
    
    // For activity mode, request standard page size (simplified)
    if (sortField === "activity") {
      console.log('🚀 [Activity] Page change - requesting tokens for activity mode');
    }
      
      emit("get-tokens", { ...requestData, clientVersion: 2 });
    }
  };

  // Show proper loading states. Gate on liveTokens (the real source of
  // truth for the grid) so that once useTokenFeed has data we drop out
  // of the loading-overlay branch and render the full success view —
  // otherwise the overlay branch renders tokens without the pagination
  // controls below and users can't navigate past page 1.
  const shouldShowLoadingOverlay = loading && tokens.length === 0 && liveTokens.length === 0;
  const shouldShowLoadingSpinner = loading && (tokens.length > 0 || liveTokens.length > 0);

  return (
    <>
      <div
        className={`app-container ${isShortScreen ? "short-screen" : ""} ${
          isVeryShortScreen ? "very-short-screen" : ""
        } ${isExtremelyShortScreen ? "extremely-short-screen" : ""}`}
      >
      {/* <PopupModal /> */}
      {/* Streaming Banner - positioned at very top */}
      {/* <StreamingBanner 
        onNewTrade={handleNewTrade} 
        sortField={sortField}
        backendConnected={isConnected}
        isSearching={isSearching}
        currentPage={currentPage} */}
        {/* key={`${sortField}-${isConnected}`} // Force re-render when key state changes */}
      {/* /> */}

      {/* <div className="static-top-section"> */}
        {/* <div className="token-dashboard"> */}

          {/* Top Tokens Section */}
          {/* <div className="top-tokens-section">
            <div className="top-tokens-grid">
              {loadingTopTokens || !highestMarketCapToken || !highestVolumeToken ? (
                <>
                  <div className="top-tokens-box">
                    <h2 className="top-token-title text-stroke-1 text-stroke-2">KING OF THE MOUNTAIN</h2>
                    <div className="token-card-new highlight-card loading-placeholder">
                      <div className="loading-content">
                        {isConnected ? "Loading..." : "Connecting..."}
                      </div>
                    </div>
                  </div>
                  <div className="top-tokens-box">
                    <h2 className="top-token-title text-stroke-1 text-stroke-2">KING OF THE JUNGLE</h2>
                    <div className="token-card-new highlight-card loading-placeholder">
                      <div className="loading-content">
                        {isConnected ? "Loading..." : "Connecting..."}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="top-tokens-box">
                    <h2 className="top-token-title text-stroke-1 text-stroke-2">KING OF THE MOUNTAIN</h2>
                    <TokenCard 
                      token={highestMarketCapToken} 
                      highlight={true} 
                      snipeMode={snipeMode}
                      snipeSettings={snipeSettings}
                      onSnipeBuy={handleSnipeBuy}
                      tokenDetails={tokenDetails}
                      loadingTokenDetails={loadingTokenDetails}
                      fetchTokenDetails={fetchTokenDetails}
                    />
                  </div>
                  <div className="top-tokens-box">
                    <h2 className="top-token-title text-stroke-1 text-stroke-2">KING OF THE JUNGLE</h2>
                    <TokenCard 
                      token={highestVolumeToken} 
                      highlight={true} 
                      snipeMode={snipeMode}
                      snipeSettings={snipeSettings}
                      onSnipeBuy={handleSnipeBuy}
                      tokenDetails={tokenDetails}
                      loadingTokenDetails={loadingTokenDetails}
                      fetchTokenDetails={fetchTokenDetails}
                    />
                  </div>
                </>
              )}
            </div>
          </div> */}

          {/* Search Bar */}
          <div className = "dashboard-page-container">
          <div className = "dashboard-page-content">
            
          <DegenHero tokens={filteredTokens.length ? filteredTokens : liveTokens} />
          <LiveFeed />
          <FeaturedTokens tokens={filteredTokens.length ? filteredTokens : liveTokens} />
          <div className="pm-section-heading pm-section-heading--all">
            <div className="pm-section-heading__title-row">
              <h2 id="all-tokens">All tokens</h2>
              <span className="pm-section-heading__rule" aria-hidden="true" />
              <div className="pm-view-toggle" role="tablist" aria-label="View mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!dexView}
                  className={`pm-view-toggle__btn ${!dexView ? "is-active" : ""}`}
                  onClick={() => setDexView(false)}
                >
                  Cards
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={dexView}
                  className={`pm-view-toggle__btn ${dexView ? "is-active" : ""}`}
                  onClick={() => setDexView(true)}
                >
                  List
                </button>
              </div>
            </div>
            <SlurpSearch tokens={liveTokens} value={searchQuery || ''} onChange={setSearchQuery} />
          </div>



            
            
            {/* <div className = "dashboard-hero-container">
              <img src = {imageDancingBanana1} className = "dashboard-hero-dancing-banana" alt = "Dancing Banana 1" />
              <div className ="dashboard-hero-middle">
                <div className ="dashboard-hero-middle-top">
                  Launch a <img className = "dashboard-hero-middle-top-logo" src = {imageLogoBanana} alt = "Banana logo" />
                </div>
                <div className ="dashboard-hero-middle-bottom">
                  <div className = "dashboard-hero-middle-bottom-item">
                    <p>No Code</p>
                    <img src = {imageBanana} alt = "Banana" />
                  </div>
                  <div className = "dashboard-hero-middle-bottom-item">
                    <p>Direct To Dex</p>
                    <img src = {imageBanana} alt = "Banana" />
                  </div>
                  <div className = "dashboard-hero-middle-bottom-item">
                    <p>Earn Fees</p>
                    <img src = {imageBanana} alt = "Banana" />
                  </div>
                </div>
              </div>
              <img src = {imageDancingBanana2} className = "dashboard-hero-dancing-banana" alt = "Dancing Banana 2" />
            </div> */}






          <div className="search-and-filter-container">

          {/* <div className="search-container-container-new">
              <div className="search-container-new">
                <img src={imageIconSearch} alt="Search" className="dashboard-search-icon" />
                <input
                  type="text"
                  className = "textbox-main dashboard-search-box"
                  placeholder="Search tokens by name or ticker"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyPress}
                />
                <button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className = {`button-main dashboard-search-button ${isSearching ? 'busy' : ''}`}
                >
                  {isSearching ? "..." : "Search"}
                </button>
                {(hasSearched || searchQuery.trim()) && (
                  <button
                    className="dashboard-clear-search-button"
                    onClick={clearSearch}
                  >
                    Clear
                  </button>
                )}
              </div>

              {hasSearched && !isSearching && (
                <div
                  style={{
                    textAlign: "center",
                    margin: "10px auto",
                    maxWidth: "500px",
                    color: "#FFB800",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                </div>
              )}
            </div> */}
            
            {/* Search bar is now in TopBar component */}
            
            <SortButtons
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
              dexView={dexView}
            />
      </div>

      <div className="token-dashboard-gallery">
        {shouldShowLoadingOverlay ? (
          // Initial first-ever load — neither legacy `tokens` nor the live
          // feed have data yet. Show a clear loader so the page doesn't
          // look frozen during the WS connect + first batch fetch.
          pagedLiveTokens.length === 0 ? (
            <div className="pm-slurp-loader" role="status" aria-live="polite" style={{ padding: 64 }}>
              <img
                src="/images/png/slurpface (1).png"
                alt="Slurp"
                style={{ width: 72, height: 72 }}
              />
              <span className="pm-slurp-loader__label">Loading tokens</span>
            </div>
          ) : dexView ? (
            <div style={{ marginTop: '20px', clear: 'both' }}>
              <DexTable
                tokens={pagedLiveTokens}
                snipeMode={snipeMode}
                snipeSettings={snipeSettings}
                onSnipeBuy={handleSnipeBuy}
                addListener={addListener}
                removeListener={removeListener}
                emit={emit}
                setShowDisclaimerModal={setShowDisclaimerModal}
              />
            </div>
          ) : (
            <div className="pm-grid">
              {pagedLiveTokens.map((token) => {
                const bump = bumpedAt[token.contractAddress];
                return (
                  <PremiumCard
                    key={token.contractAddress}
                    token={token}
                    bumpKind={bump?.kind || null}
                    bumpTs={bump?.ts || 0}
                    onQuickBuy={(addr, name, sym, chain, eth) =>
                      handleSnipeBuy?.(addr, name, sym, chain, eth)
                    }
                    onCopy={copyToClipboard}
                  />
                );
              })}
            </div>
          )
        ) : error ? (
          <div className="error-message">
            {error}
            {stats.totalTokens > 0 && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "20px",
                  background: "rgba(255, 184, 0, 0.1)",
                  borderRadius: "8px",
                }}
              >
                <h3>Global Statistics</h3>
                <p>Total Tokens: {stats.totalTokens}</p>
                <p>
                  Total Volume: ${stats.totalVolume?.toLocaleString() || "N/A"}
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {dexView ? (
              <div style={{ marginTop: '20px', clear: 'both' }}>
                <DexTable
                  tokens={pagedTokens}
                  snipeMode={snipeMode}
                  snipeSettings={snipeSettings}
                  onSnipeBuy={handleSnipeBuy}
                  addListener={addListener}
                  removeListener={removeListener}
                  emit={emit}
                  setShowDisclaimerModal={setShowDisclaimerModal}
                />
              </div>
            ) : (
              <div className="pm-grid">
                {pagedTokens.length === 0 ? (
                  <div className="pm-empty">
                    {hasSearched
                      ? `No tokens match "${searchQuery}"`
                      : isSearching
                      ? "Searching…"
                      : "No tokens available."}
                  </div>
                ) : (
                  pagedTokens.map((token) => {
                    const bump = bumpedAt[token?.contractAddress] || bumpedAt[token?.mint];
                    return (
                      <PremiumCard
                        key={token.contractAddress}
                        token={token}
                        bumpKind={bump?.kind || null}
                        bumpTs={bump?.ts || 0}
                        onQuickBuy={(addr, name, sym, chain, eth) =>
                          handleSnipeBuy?.(addr, name, sym, chain, eth)
                        }
                        onCopy={copyToClipboard}
                      />
                    );
                  })
                )}
              </div>
            )}

            {/* Mobile spacer - will only show on mobile */}
            {/* <div className="mobile-spacer"></div> */}

            {/* Extra spacing div with inline style for mobile */}
            {/* {isMobile && <div style={{ height: "80px", width: "100%" }}></div>} */}

            {/* Pagination Controls */}
            <div
              className="pagination-controls-new"
              // style={{
              //   marginTop: isMobile ? "80px" : "20px",
              // }}
            >
              {hasSearched ? (
                // Search results pagination
                <>
                  <button
                    onClick={() => handleSearchPageChange(1)}
                    disabled={searchResultsPage === 1 || isSearching}
                    className = "button-main button-pagination"
                  >
                    {/* <img className="button-pagination-img button-first-img" src={imageIconLast} alt="First" /> */}
                  {"<<"}
                  </button>
                  <button
                    onClick={() =>
                      handleSearchPageChange(searchResultsPage - 1)
                    }
                    disabled={searchResultsPage === 1 || isSearching}
                    className = "button-main button-pagination"
                  >
                  {"<"}

                    {/* <img className="button-pagination-img button-previous-img" src={imageIconNext} alt="Previous" /> */}

                  </button>
                  <span className="page-info-new">
                    Page {searchResultsPage} of {searchResultsTotalPages}
                  </span>
                  <button
                    onClick={() =>
                      handleSearchPageChange(searchResultsPage + 1)
                    }
                    disabled={
                      searchResultsPage === searchResultsTotalPages ||
                      isSearching
                    }
                    className = "button-main button-pagination"
                  >
                    {/* <img className="button-pagination-img button-next-img" src={imageIconNext} alt="Next" /> */}
                    {">"}

                  </button>
                  <button
                    onClick={() =>
                      handleSearchPageChange(searchResultsTotalPages)
                    }
                    disabled={
                      searchResultsPage === searchResultsTotalPages ||
                      isSearching
                    }
                    className = "button-main button-pagination"
                  >
                  {">>"}

                    {/* <img className="button-pagination-img button-last-img" src={imageIconLast} alt="Last" /> */}
                  </button>
                </>
              ) : (
                // Normal token pagination
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={safePage === 1 || isSearching}
                    className = "button-main button-pagination"
                  >
                    {/* <img className="button-pagination-img button-first-img" src={imageIconLast} alt="First" /> */}
 {"First"}
                  </button>
                  <button
                    onClick={() => handlePageChange(safePage - 1)}
                    disabled={safePage === 1 || isSearching}
                    className = "button-main button-pagination arrow"
                  >
                    {/* <img className="button-pagination-img button-previous-img" src={imageIconNext} alt="Previous" /> */}
                    {"<"}

                  </button>
                  <span className="page-info-new">
                    {safePage} of {displayedTotalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(safePage + 1)}
                    disabled={safePage >= displayedTotalPages || isSearching}
                    className = "button-main button-pagination arrow"
                  >
                    {/* <img className="button-pagination-img button-next-img" src={imageIconNext} alt="Next" /> */}
                    {">"}

                  </button>
                  <button
                    onClick={() => handlePageChange(displayedTotalPages)}
                    disabled={safePage >= displayedTotalPages || isSearching}
                    className = "button-main button-pagination"
                  >
                    {/* <img className="button-pagination-img button-last-img" src={imageIconLast} alt="Last" /> */}
                    {"Last"}
                  
                  </button>
                </>
              )}
            </div>

          </>
        )}
      </div>
    </div>

    </div>
    </div>
    
    {/* Snipe Settings Modal */}
    <SnipeModal
      isOpen={showSnipeModal}
      onClose={() => setShowSnipeModal(false)}
      snipeSettings={snipeSettings}
      setSnipeSettings={setSnipeSettings}
      setSnipeMode={setSnipeMode}
    />

    {/* Disclaimer Modal */}
    <DisclaimerModal
      isOpen={showDisclaimerModal}
      onClose={() => setShowDisclaimerModal(false)}
    />
    
    {/* Network Warning Modal */}
    {showNetworkModal && (
      <div className="modal-overlay" onClick={() => setShowNetworkModal(false)}>
        <div className="modal-content network-modal">
          <div className="modal-header">
            <h2>🌐 Wrong Network!</h2>
            <button className="modal-close-btn" onClick={() => setShowNetworkModal(false)}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="network-warning-content">
              <p className="network-warning-text">
                You're currently on <strong>{networkModalData.currentChain}</strong> but this token is on <strong>{networkModalData.expectedChain}</strong>.
              </p>
              
              <div className="network-warning-instruction">
                <p>Please switch to <strong>{networkModalData.expectedChain}</strong> network in your wallet to continue.</p>
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => setShowNetworkModal(false)}>
              Got it
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function getSymbolSizeClass(symbolText)
{
  if (symbolText.length >= 10)
  {
    return "token-card-symbol-name-chars-10";
  }
  else if (symbolText.length >= 8)
    {
      return "token-card-symbol-name-chars-8";
    }
  else if (symbolText.length >= 7)
  {
    return "token-card-symbol-name-chars-7";
  }
  else if (symbolText.length >= 6)
  {
    return "token-card-symbol-name-chars-6";
  }
}

// Disclaimer Modal Component
const DisclaimerModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content disclaimer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>LEGAL DISCLAIMER</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body disclaimer-content">
          <div className="disclaimer-section">
            <h3>IMPORTANT NOTICE - READ CAREFULLY</h3>
            <p>By accessing, using, or participating in King Of Apes (the "Platform"), you acknowledge that you have read, understood, and agree to be bound by this disclaimer and all associated terms and conditions.</p>
          </div>

          <div className="disclaimer-section">
            <h3>GENERAL DISCLAIMER</h3>
            <p>The information provided on this Platform is for general informational purposes only and does not constitute financial, investment, legal, or tax advice. King Of Apes is a software platform that facilitates the launch and distribution of digital tokens and does not provide investment advisory services.</p>
          </div>

          <div className="disclaimer-section">
            <h3>INVESTMENT RISKS</h3>
            <h4>High-Risk Investment</h4>
            <p>Digital tokens and cryptocurrencies are highly speculative and involve substantial risk of loss. Token values can fluctuate dramatically and may become worthless. You should only invest what you can afford to lose completely.</p>
            
            <h4>No Guarantee of Returns</h4>
            <p>Past performance of tokens launched on our Platform does not predict future results. There are no guarantees of profits, and all investments may result in partial or total loss.</p>
            
            <h4>Regulatory Risk</h4>
            <p>Cryptocurrency regulations are evolving and may change rapidly. Regulatory actions could negatively impact token values or restrict access to the Platform or tokens.</p>
          </div>

          <div className="disclaimer-section">
            <h3>PLATFORM DISCLAIMERS</h3>
            <h4>No Endorsement</h4>
            <p>King Of Apes does not endorse, recommend, or guarantee any project, token, or investment opportunity listed on the Platform. All token launches are third-party projects, and we do not verify the accuracy of project claims or representations.</p>
            
            <h4>Due Diligence Required</h4>
            <p>Users are solely responsible for conducting their own research and due diligence before participating in any token launch. This includes verifying project legitimacy, reviewing technical documentation, and assessing risk factors.</p>
            
            <h4>Technology Risks</h4>
            <p>Blockchain technology and smart contracts may contain bugs, vulnerabilities, or design flaws that could result in loss of funds. The Platform and associated smart contracts are provided "as is" without warranties.</p>
          </div>

          <div className="disclaimer-section">
            <h3>USER RESPONSIBILITIES</h3>
            <h4>Eligibility</h4>
            <p>You represent that you are legally permitted to use the Platform and participate in token launches in your jurisdiction. You are responsible for compliance with all applicable laws and regulations.</p>
            
            <h4>Prohibited Jurisdictions</h4>
            <p>The Platform may not be available to residents of certain jurisdictions. Users from restricted jurisdictions are prohibited from accessing or using the Platform.</p>
            
            <h4>Tax Obligations</h4>
            <p>You are solely responsible for determining and fulfilling all tax obligations related to your use of the Platform and any token transactions.</p>
          </div>

          <div className="disclaimer-section">
            <h3>LIMITATION OF LIABILITY</h3>
            <h4>No Warranties</h4>
            <p>The Platform is provided "as is" and "as available" without warranties of any kind, whether express or implied, including but not limited to merchantability, fitness for a particular purpose, or non-infringement.</p>
            
            <h4>Liability Limitation</h4>
            <p>To the maximum extent permitted by law, King Of Apes and its affiliates, officers, directors, employees, and agents shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of the Platform.</p>
            
            <h4>Force Majeure</h4>
            <p>King Of Apes shall not be liable for any failure or delay in performance due to circumstances beyond its reasonable control, including but not limited to network congestion, blockchain forks, or regulatory actions.</p>
          </div>

          <div className="disclaimer-section">
            <h3>THIRD-PARTY CONTENT</h3>
            <h4>Project Information</h4>
            <p>Information about token projects is provided by third parties and has not been independently verified by King Of Apes. We do not guarantee the accuracy, completeness, or reliability of such information.</p>
            
            <h4>External Links</h4>
            <p>The Platform may contain links to third-party websites or services. We do not control and are not responsible for the content or practices of such third parties.</p>
          </div>

          <div className="disclaimer-section">
            <h3>INTELLECTUAL PROPERTY</h3>
            <p>All content, trademarks, and intellectual property on the Platform are owned by King Of Apes or licensed third parties. Users may not reproduce, distribute, or create derivative works without express written permission.</p>
          </div>

          <div className="disclaimer-section">
            <h3>PRIVACY AND DATA</h3>
            <p>Your use of the Platform is subject to our Privacy Policy. By using the Platform, you consent to the collection, use, and disclosure of your information as described in our Privacy Policy.</p>
          </div>

          <div className="disclaimer-section">
            <h3>MODIFICATIONS</h3>
            <p>King Of Apes reserves the right to modify this disclaimer and the Platform's terms of use at any time without prior notice. Continued use of the Platform constitutes acceptance of such modifications.</p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenDashboard;
